// Monorepo-level shared types used by both backend and frontend.
// Backend: import via @corner/shared (tsconfig paths + tsconfig-paths/register)
// Frontend: import via @corner/shared (vite resolve alias)

export type ToolName =
  // PDF ↔ Office
  | 'pdf_to_word'
  | 'pdf_to_jpg'
  | 'pdf_to_png'
  | 'word_to_pdf'
  | 'excel_to_pdf'
  | 'pptx_to_pdf'
  | 'jpg_to_pdf'
  | 'png_to_pdf'
  // PDF utilities
  | 'merge_pdf'
  | 'split_pdf'
  | 'compress_pdf'
  | 'rotate_pdf'
  | 'add_page_numbers'
  | 'password_protect_pdf'
  | 'remove_pdf_password'
  | 'add_watermark_pdf'
  | 'repair_pdf'
  | 'ocr'
  | 'esign'
  // Image tools
  | 'remove_background'
  | 'resize_image'
  | 'flip_rotate_image'
  | 'add_border_image'
  | 'watermark_image'
  | 'image_to_pdf'
  | 'compress_image'
  | 'convert_image'
  // Image format conversions
  | 'jpg_to_png'
  | 'png_to_jpg'
  | 'webp_to_jpg'
  | 'jpg_to_webp'
  // Office utilities
  // Misc
  | 'generate_qr'
  | 'extract_text'
  // Document intelligence (study + citation)
  | 'summarize_document'
  | 'generate_study_questions'
  | 'extract_key_terms'
  | 'generate_citation'
  // Audio
  | 'transcribe_audio'
  // Signing + form filling
  | 'sign_document'
  | 'fill_pdf_form'
  // Document editing
  | 'plan_doc_edits'
  // Document generation (from scratch, no input file needed)
  | 'generate_document'

/** Tier 2 analysis-only intents (prompt-based; no file tool). */
export type AnalysisType =
  | 'summarize'
  | 'study_questions'
  | 'key_terms'
  | 'citation_generator'
  | 'contract_review'
  | 'action_items'
  | 'email_draft'
  | 'sensitive_data'
  | 'document_draft';

export const ANALYSIS_TYPES: AnalysisType[] = [
  'summarize',
  'study_questions',
  'key_terms',
  'citation_generator',
  'contract_review',
  'action_items',
  'email_draft',
  'sensitive_data',
  'document_draft',
];

export interface ParsedIntentParams {
  input_type?: string;
  output_type?: string;
  options?: Record<string, unknown>;
  analysisType?: AnalysisType;
}

export interface ParsedIntentStep {
  tool: string;
  params: Record<string, unknown>;
  description: string;
}

export type ExecutionMode = 'silent' | 'interactive' | 'analysis';

export interface ParsedIntent {
  tool: ToolName | null;
  params: ParsedIntentParams;
  mode: ExecutionMode;
  confidence: number;
  clarification: string | null;
  intent: string;
  steps: ParsedIntentStep[];
  /** File format badge resolved from the filename extension — injected by the backend, not Claude. */
  fileExtension?: string;
}

/** Study tools that return inline text + optional export (no doc switch) */
export type StudyToolName = 'summarize_document' | 'generate_study_questions' | 'extract_key_terms';

/** Client-facing result after tool execution */
export interface SplitFileClientEntry {
  fileName: string;
  fileId: string;
  downloadUrl: string;
  sizeBytes: number;
}

export interface MergeManifestEntry {
  fileName: string;
  pageCount: number;
  startPage: number;
  endPage: number;
}

export interface WalkthroughRegion {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WalkthroughStep {
  id: string;
  title: string;
  description: string;
  region?: WalkthroughRegion;
  kind?: string;
}

export interface ToolResult {
  fileId: string;
  downloadUrl: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /**
   * Discriminator for how the result body is sourced.
   *
   * - `'file'` (default, omitted on legacy results): body lives in object
   *   storage at `/api/file/:fileId`. Most tools produce these.
   * - `'document'`: body is an in-conversation TipTap-style HTML document
   *   stored in `Conversation.documents[]`. Resolve via
   *   `GET /api/conversations/:cid/documents/:documentId` or by looking up
   *   `documentId` in the session payload's `documents` array. Drafts
   *   created by `draft-stream` use this kind so the body always lives in
   *   one canonical place (no orphan synthetic `draft-*` ids).
   */
  kind?: 'file' | 'document';
  /** When `kind === 'document'`, the in-conversation document id. */
  documentId?: string;
  /** When `kind === 'document'`, the version index of that document. */
  documentVersion?: number;
  /** File ID of the original uploaded source when the backend preserved it. */
  sourceFileId?: string;
  previewUrl?: string;
  /** Optional confirmation copy returned by direct execute flows. */
  message?: string;
  /** For study tools: full text to show inline in chat */
  textContent?: string;
  /** For document_draft: rich HTML content for the inline editor */
  draftHtml?: string;
  /** For document_draft: suggested title for the drafted document */
  draftTitle?: string;
  /** For study tools / Tier 2 analysis: which tool or analysis type produced this result */
  studyTool?: StudyToolName | AnalysisType;
  /** Optional guided UI walkthrough metadata for result viewers. */
  walkthrough?: WalkthroughStep[];
  /** Staged document edits awaiting user accept/reject decisions. */
  editBatch?: EditBatch;
  transcriptionResult?: {
    transcript: string;
    duration: number;
    language: string;
    wordCount: number;
    speakerCount?: number;
    segmentCount: number;
    durationLabel: string;
  };
  formattedTranscript?: string;
  durationLabel?: string;
  /** Populated by merge_pdf — per-source-file page ranges for the consolidated preview. */
  mergeManifest?: MergeManifestEntry[];
  /** Wall-clock time to produce a merged PDF, used in the chat result card. */
  mergeDurationMs?: number;
  /** Populated by split_pdf — individual page files with download URLs. */
  splitFiles?: SplitFileClientEntry[];
  /** Populated by split_pdf — page count for zip output summary UI. */
  splitPageCount?: number;
  /** Wall-clock time to produce split PDFs, used in the chat result card (Paper 5SJ). */
  splitDurationMs?: number;
  /** Populated by ocr tool — Tesseract confidence (0–100) and timing for chat card (Paper 5ZD). */
  ocrMeta?: {
    confidence: number;
    durationMs: number;
  };
  /** generate_qr — encoded payload + dimensions for chat card (Paper 667). */
  qrMeta?: {
    payload: string;
    sizePx: number;
    format: 'png' | 'svg';
    /** When format is png, server may include SVG source for instant alternate download */
    svgContent?: string;
  };
  /** add_watermark_pdf — watermark text + page count for chat result card (Paper 6BD). */
  watermarkMeta?: { text: string; pageCount: number };
  /** add_page_numbers — position label + page count for chat result card (Paper 6I7). */
  pageNumbersMeta?: { position: string; pageCount: number };
  /** Tool-specific status. E-sign: signed/no_fields_found/etc. Fill: filled/missing_values.
   *  'detection_unavailable' fires when the underlying field-detection service
   *  errored out — distinct from 'no_fields_found' which means we successfully
   *  scanned the doc and confirmed it has no fields. Frontend uses this to
   *  show an honest "service unavailable" message instead of misleading the
   *  user into thinking their document genuinely has no signature fields. */
  status?: 'signed' | 'needs_signature_setup' | 'no_fields_found' | 'role_selection_needed' | 'filled' | 'missing_values' | 'detection_unavailable';
  fields?: SignatureField[];
  selfFields?: SignatureField[];
  otherFields?: SignatureField[];
  signedPdfBuffer?: string;
  /** Available signing roles for role_selection_needed status (e.g. ['Buyer', 'Agent']). */
  availableRoles?: string[];
  /** Structured list of fields that still need values (fill_pdf_form). */
  missingFields?: Array<{ role: string; label: string; valueSource: string; page: number }>;
  /** Per-field values applied by fill_pdf_form (server side). The frontend
   *  mirrors these into placeholderValuesStore so the live overlay reflects
   *  the AI/profile fill instantly without re-downloading the rewritten PDF. */
  filledPlaceholders?: Array<{ fieldId: string; value: string; source: 'profile' | 'date' | 'user' | 'ai' }>;
  /** Inferred document type (e.g. 'saas_msa', 'real_estate_buyer_rep_agreement'). */
  documentType?: DocumentType;
}

export type WorkspaceSessionMode = 'empty' | 'processing' | 'result' | 'esign' | 'clarifying' | 'drafting' | 'review';

export interface WorkspaceSessionDocumentRef {
  fileId?: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl?: string | null;
  source: 'upload' | 'attachment' | 'source_file' | 'result' | 'generated';
}

export interface WorkspaceSessionPanelState {
  chatCollapsed: boolean;
  focusedDocumentView: boolean;
  canvasViewMode: boolean;
  rightPanelOpen: boolean;
  previewMode?: 'page' | 'text' | 'frames';
  zoom?: number;
  documentPreviewVariant?: 'original' | 'output';
}

export interface WorkspaceSessionTaskContext {
  lastToolName?: string | null;
  modeLabel?: string | null;
  runId?: string | null;
  workflowId?: string | null;
}

export interface WorkspaceSessionCotStep {
  title: string;
  status: string;
  description?: string;
  category?: string;
  editBatch?: EditBatch;
  [key: string]: unknown;
}

export interface WorkspaceSessionSnapshot {
  conversationId: string;
  title?: string | null;
  projectId?: string | null;
  activeVersionId?: string | null;
  mode: WorkspaceSessionMode;
  primaryDocument: WorkspaceSessionDocumentRef | null;
  sourceDocuments: WorkspaceSessionDocumentRef[];
  currentResult: ToolResult | null;
  lastConversionOutput: ToolResult | null;
  panelState: WorkspaceSessionPanelState;
  taskContext?: WorkspaceSessionTaskContext | null;
  cotSteps?: WorkspaceSessionCotStep[];
  updatedAt: string;
}

/**
 * In-conversation document — a typed body that lives on the Conversation
 * itself rather than in object storage. This is the canonical home for
 * draft HTML produced by `draft-stream`; replaces the synthetic
 * `draft-${conversationId}` fileId pattern that had no actual storage and
 * could end up dangling if the snapshot was persisted before the body
 * landed (the historical "orphan draft shell" hydration bug).
 *
 * Inspired by Boltzman's `canvases[]` model: the document body is stored
 * exactly where the pointer says it lives, eliminating the snapshot/body
 * sync class of failures by construction.
 */
export interface ConversationDocument {
  id: string;
  title: string;
  html: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** The user prompt that produced this document (when generated). */
  originPrompt?: string | null;
}

/**
 * Lifecycle of an in-flight or completed draft generation. Persisted on
 * the conversation so a hydration after a tab close / network drop /
 * full reload can see "the user submitted but the LLM never finished"
 * and auto-resume — no live SSE retry needed. Borrowed from Boltzman's
 * `status: 'initial'` resume protocol.
 */
export type DraftStatus =
  | 'idle'        // no draft in progress
  | 'pending'     // user submitted; LLM call hasn't streamed yet
  | 'streaming'   // tokens are arriving
  | 'complete'    // last draft finished cleanly; result lives in documents[]
  | 'failed';     // last attempt errored; safe to retry

/** Arguments needed to relaunch an interrupted draft generation. */
export interface PendingDraft {
  prompt: string;
  currentDraftHtml?: string | null;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  /** ISO timestamp of when generation was queued. */
  since: string;
  /** How many auto-resume attempts have already been made. */
  attempts?: number;
}

export type DocumentVersionSource =
  | 'original'
  | 'upload'
  | 'generated'
  | 'ai_edit'
  | 'signed'
  | 'workflow'
  | 'conversion'
  | 'manual';

/** Iteration history vs duplicate file slots saved from the same draft. */
export type DocumentVersionLineageKind = 'version' | 'copy';

export interface DocumentVersionSummary {
  id: string;
  projectId: string;
  versionNumber: number;
  fileId: string;
  fileName: string;
  mimeType: string;
  source: DocumentVersionSource;
  /** Defaults to `version` when omitted (legacy rows). */
  lineageKind?: DocumentVersionLineageKind;
  createdBy: string;
  createdFromVersionId?: string | null;
  changeSummary?: string | null;
  conversationId?: string | null;
  createdAt: string;
}

export interface DocumentProjectSummary {
  id: string;
  title: string;
  folderId?: string | null;
  primaryConversationId: string;
  currentVersionId?: string | null;
  currentVersion?: DocumentVersionSummary | null;
  versionCount: number;
  createdFrom: 'conversation' | 'upload' | 'workflow' | 'manual';
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentProjectDetail extends DocumentProjectSummary {
  versions: DocumentVersionSummary[];
  conversations: Array<{
    id: string;
    title: string;
    lastMessageAt: string;
  }>;
}

export interface VersionVerificationChange {
  id: string;
  kind: 'added' | 'removed' | 'changed';
  before?: string;
  after?: string;
  summary: string;
}

export interface VersionVerificationResult {
  baseVersionId: string;
  targetVersionId: string;
  changedSections: VersionVerificationChange[];
  unexpectedChanges: VersionVerificationChange[];
  expectedChangeMatched: boolean;
  summary: string;
}

// CORNER_V2: SignatureField is the canonical field type for signing UX.
// Coordinates are PERCENT-BASED (0-100) — confirmed across all models
// (DocumentFieldMap, DocumentField, DetectedDocField). No conversion needed.
export interface SignatureField {
  id?: string;
  type?: 'signature' | 'initials' | 'date' | 'text';
  party?: 'self' | 'other';
  page: number;
  /** Percent from left edge of page (0-100). */
  x: number;
  /** Percent from top edge of page (0-100). */
  y: number;
  /** Percent of page width (0-100). */
  width: number;
  /** Percent of page height (0-100). */
  height: number;
  label: string;
  required?: boolean;
  placed?: boolean;
  /** Timestamp when this field was completed. Set by backend processSignature for invite flow. */
  signedAt?: number;
  signer?: 'you' | 'other';
  /** Email of the signer who completed this field (multi-party invite flow). */
  signedByEmail?: string;
  /** Index into SigningRequest.signers[] that owns this field. Set at request creation. */
  assignedSignerIndex?: number;
  // CORNER_V2: Candidate metadata (from SignatureTargetCandidate detection pipeline).
  // Optional — present when the field was detected with confidence scoring.
  /** Detection confidence 0-1 (from documentFieldMapService). Higher = more reliable placement. */
  confidence?: number;
  /** Party role hint from detection context (e.g., 'buyer', 'agent', 'seller'). */
  partyHint?: string;
  /** Semantic role from document field map (e.g., 'buyer_signature', 'agent_date'). */
  role?: string;
  /** For text placeholders (inline_placeholder regions): how the slot was
   *  visually delimited. Drives the redact-and-draw UX: bracketed/highlighted
   *  placeholders get an opaque overlay covering the original ink; underline
   *  fields keep their underscore visible. */
  placeholderStyle?: 'square' | 'curly' | 'angle' | 'paren' | 'highlight' | 'underline' | 'anchor';
  /** Raw placeholder text (e.g. "[BUYER NAME]"). Used by the live overlay
   *  to know what visual content is being replaced. */
  placeholderRaw?: string;
  /** Parsed role from anchor tag (e.g. 'buyer' from [[SIG:buyer]]). Set only when placeholderStyle === 'anchor'. */
  anchorRole?: string;
  /** Parsed type from anchor tag. */
  anchorType?: 'signature' | 'date' | 'name' | 'initials' | 'text';
  /** v1 placement-redesign: visual state for pre-send markers.
   *  - 'suggested': AI/default placement, user has not vetted (orange)
   *  - 'confirmed': user has dragged or explicitly placed (green outline)
   *  - 'signed': recipient has actually signed it (filled green)
   *  Missing/undefined is treated as 'suggested' for backward compat. */
  state?: 'suggested' | 'confirmed' | 'signed';
  /**
   * Free text for title/role lines (e.g. "CEO", "Co-Founder"). Used when
   * `isTitleMarkerField` is true; name/printed-name fields still use the
   * account display name unless this is set elsewhere.
   */
  printedText?: string;
}

export type SigningFieldSizeKind = 'signature' | 'name' | 'date' | 'initial' | 'title';

export interface SigningFieldSize {
  width: number;
  height: number;
}

export const SIGNING_FIELD_SIZES: Record<SigningFieldSizeKind, SigningFieldSize> = {
  signature: { width: 18, height: 4 },
  name: { width: 14, height: 3 },
  date: { width: 8, height: 2.4 },
  initial: { width: 5, height: 2.8 },
  title: { width: 10, height: 2.4 },
};

export function signingFieldSizeForKind(kind: SigningFieldSizeKind): SigningFieldSize {
  return SIGNING_FIELD_SIZES[kind];
}

/** Title markers from the toolbar use `role` suffix `_title` or labels containing "title" (not name). */
export function isTitleMarkerField(field: Pick<SignatureField, 'role' | 'label' | 'type'>): boolean {
  const role = field.role ?? '';
  if (role.endsWith('_title')) return true;
  const labelLower = (field.label ?? '').toLowerCase();
  if (/\bname\b|printed\s*name|full\s*name/.test(labelLower)) return false;
  return field.type === 'text' && /\btitle\b/.test(labelLower);
}

export function signingFieldSizeForField(field: Pick<SignatureField, 'role' | 'label' | 'type'>): SigningFieldSize {
  if (field.type === 'date') return signingFieldSizeForKind('date');
  if (field.type === 'initials') return signingFieldSizeForKind('initial');
  if (field.type === 'text') return signingFieldSizeForKind(isTitleMarkerField(field) ? 'title' : 'name');
  return signingFieldSizeForKind('signature');
}

// CORNER_V2: SigningSlot is the canonical abstraction for "where each party signs".
// Generated once when a SigningSession is prepared, reused by all signers.
// The AI/detection pipeline runs once to produce these; subsequent signers consume them directly.
export type SigningSlotStatus = 'pending' | 'signed';

export interface SigningSlotRect {
  /** Percent from left edge (0-100). */
  x: number;
  /** Percent from top edge (0-100). */
  y: number;
  /** Percent of page width (0-100). */
  width: number;
  /** Percent of page height (0-100). */
  height: number;
}

export interface SigningSlot {
  /** Stable UUID for this slot. */
  id: string;
  docId: string;
  pageNumber: number;
  rect: SigningSlotRect;
  type: 'signature' | 'initials' | 'date' | 'text';
  /** Semantic role (e.g., 'buyer_signature', 'agent_date'). */
  role?: string;
  /** Human-readable label (e.g., 'Buyer Signature'). */
  label?: string;
  /** Which party must fill this slot (email, role key, or signer index). */
  partyId: string;
  status: SigningSlotStatus;
  /** How this slot was created — for debugging and audit. */
  createdFrom: {
    source: 'field' | 'candidate' | 'manual';
    signatureFieldId?: string;
    candidateId?: string;
  };
  /** Detection confidence 0-1 (from SignatureTargetCandidate pipeline). */
  confidence?: number;
  /** Party role hint from detection (e.g., 'buyer', 'agent'). */
  partyHint?: string;
}

export type DocFieldType = 'signature' | 'initials' | 'text' | 'date' | 'checkbox';

// ─── Document type profiles ─────────────────────
export type DocumentType =
  | 'real_estate_buyer_rep_agreement'
  | 'saas_msa'
  | 'nda'
  | 'employment_agreement'
  | 'academic_assignment'
  | 'academic_paper'
  | 'research_proposal'
  | 'legal_engagement_letter'
  | 'legal_service_agreement'
  | 'legal_commercial_lease'
  | 'generic_contract';

export type DocFieldRole =
  | 'buyer_signature'
  | 'seller_signature'
  | 'agent_signature'
  | 'landlord_signature'
  | 'tenant_signature'
  | 'employer_signature'
  | 'employee_signature'
  | 'buyer_date'
  | 'seller_date'
  | 'agent_date'
  | 'landlord_date'
  | 'tenant_date'
  | 'employer_date'
  | 'employee_date'
  | 'buyer_name'
  | 'seller_name'
  | 'agent_name'
  | 'landlord_name'
  | 'tenant_name'
  | 'employer_name'
  | 'employee_name'
  | 'brokerage_name'
  | 'brokerage_address'
  | 'property_city_area'
  | 'property_address'
  | 'property_type'
  | 'buyer_address'
  | 'seller_address'
  | 'commission_percent'
  | 'agreement_date'
  | 'start_date'
  | 'end_date'
  | 'termination_notice_days'
  | 'min_price'
  | 'max_price'
  | 'notice_days'
  | 'bedrooms_count'
  | 'bathrooms_count'
  | 'square_footage'
  | 'lot_size'
  // ── Cross-domain / SaaS / generic contract roles ──
  | 'company_name'
  | 'counterparty_name'
  | 'company_address'
  | 'counterparty_address'
  | 'effective_date'
  | 'initial_term'
  | 'renewal_term'
  | 'notice_period_days'
  | 'governing_law_state'
  | 'subscription_fee'
  | 'usage_cap'
  | 'overage_rate'
  | 'liability_cap'
  | 'service_level_target'
  | 'cure_period_days'
  | 'confidentiality_period'
  | 'indemnification_cap'
  | 'payment_terms_days'
  // ── Academic / student roles ──
  | 'student_name'
  | 'student_id'
  | 'course_name'
  | 'course_code'
  | 'instructor_name'
  | 'term'
  | 'institution_name'
  | 'department_name'
  | 'assignment_title'
  | 'paper_title'
  | 'due_date'
  | 'submission_date'
  | 'abstract_section'
  | 'research_question'
  | 'hypothesis'
  | 'method_summary'
  | 'results_summary'
  | 'conclusion_section'
  | 'citation_placeholder'
  | 'figure_placeholder'
  | 'table_placeholder'
  // ── Legal / small firm roles ──
  | 'client_name'
  | 'client_address'
  | 'law_firm_name'
  | 'law_firm_address'
  | 'matter_name'
  | 'matter_description'
  | 'retainer_amount'
  | 'hourly_rate'
  | 'flat_fee_amount'
  | 'jurisdiction'
  | 'venue'
  | 'dispute_resolution'
  | 'arbitration_forum'
  | 'notice_address_client'
  | 'notice_address_firm'
  | 'rent_amount'
  | 'security_deposit'
  | 'premises_address'
  // ── Fallback ──
  | 'generic_text'
  | 'generic'
  // ── Dynamic roles discovered by Reducto ──
  // Reducto reads the document and returns whatever party labels it
  // sees (e.g. "Author_signature", "Witness_initials", "Resident_date").
  // The (string & {}) trick keeps autocomplete on the named roles above
  // while still accepting arbitrary strings at runtime — required so
  // we don't have to hardcode a role taxonomy that drifts from reality.
  | (string & {});

export type PlaceholderValueSource =
  | 'signer_profile'
  | 'counterparty_profile'
  | 'current_date'
  | 'computed_date'
  | 'document_constant'
  | 'system_default'
  | 'user_input'
  | 'external_system'
  | 'llm_inferred'
  | 'unknown';

export interface DocFieldBBox {
  /** Percent from the left edge of the page (0-100). */
  x: number;
  /** Percent from the top edge of the page (0-100). */
  y: number;
  /** Percent of total page width (0-100). */
  width: number;
  /** Percent of total page height (0-100). */
  height: number;
}

/** Detection signals that contributed to a field's identification and confidence score. */
export interface DetectionSignals {
  hasLabelKeyword?: boolean;      // "Signature", "Signed", "By:", "Date", etc.
  hasPlaceholderToken?: boolean;  // [SIGNATURE], [DATE], etc.
  hasUnderline?: boolean;         // _____, ....., -----
  isLineBelowLabel?: boolean;     // standalone underline below a label line
  isInSignatureBlock?: boolean;   // grouped with Signature + Print Name + Date
  nearbyRoleText?: string;        // "Buyer", "Client", "Agent", etc.
  bboxSource?: 'placeholder' | 'underline' | 'inline_estimate' | 'below_estimate' | 'widget' | 'split_range';
  /** How the placeholder is delimited visually. Used by redact-and-draw fill
   *  path to know whether to paint over brackets/highlights. */
  placeholderStyle?: 'square' | 'curly' | 'angle' | 'paren' | 'highlight' | 'underline' | 'anchor';
  /** Raw text inside the placeholder ("BUYER NAME" for [BUYER NAME]). Used by
   *  the frontend overlay to know what to visually replace. */
  placeholderRaw?: string;
  /** RGB triplet (0-1) of the highlight color when placeholderStyle === 'highlight'. */
  highlightColor?: [number, number, number];
  /** Parsed role from anchor tag (e.g. 'buyer' from [[SIG:buyer]]). Set only when placeholderStyle === 'anchor'. */
  anchorRole?: string;
  /** Parsed type from anchor tag. */
  anchorType?: 'signature' | 'date' | 'name' | 'initials' | 'text';
}

export interface DetectedDocField {
  id: string;
  /** 1-indexed page number for consistency with the existing signing flow. */
  page: number;
  bbox: DocFieldBBox;
  type: DocFieldType;
  role: DocFieldRole;
  /** Where the value should come from (signer_profile, user_input, current_date, etc.) */
  valueSource?: PlaceholderValueSource;
  labelText?: string;
  rawText?: string;
  confidence?: number;
  /** Whether this field must be filled before the document can be completed. Defaults to true. */
  isRequired?: boolean;
  /** Whether this field is an interactive signing marker (true) or a text autofill placeholder (false).
   *  Set by AI refinement. If undefined (old cached data), treated as interactive for backward compat. */
  isInteractive?: boolean;
  /** Signals that contributed to this detection — used for debugging and confidence tuning. */
  signals?: DetectionSignals;
}

export interface DocFieldMap {
  fileId: string;
  fields: DetectedDocField[];
  createdAt: string;
  // 'ai_v2' is the v2 hybrid pipeline (deterministic regions + AI classification +
  // deterministic geometry). Older sources are kept for backward compatibility but
  // treated as cache misses by getOrCreateDocFieldMap so they get re-detected.
  source: 'ocr' | 'pdf_form_fields' | 'hybrid' | 'ai' | 'ai_v2';
  // Set to true when the detection backend (Reducto) returned an error or
  // timed out. fields[] will be empty in this case. Lets downstream tools
  // distinguish "the document genuinely has no signature/text fields"
  // (detectionFailed=false, fields=[]) from "we never saw the document"
  // (detectionFailed=true, fields=[]). This flag is NOT persisted to the
  // DocumentFieldMap cache — a failed result is transient and shouldn't
  // be cached as the "right answer" for future calls.
  detectionFailed?: boolean;
}

export type PlanTier = 'free' | 'pro';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  plan: PlanTier;
  emailVerified: boolean;
  signatureId?: string | null;
  initialsId?: string | null;
  signatureVerified?: boolean;
  updatedAt?: string;
}

export interface AuthResponse {
  accessToken: string;
  // refreshToken is set as an httpOnly cookie by the server — not in the response body
  user: AuthUser;
}

export interface TokenRefreshResponse {
  accessToken: string;
  // refreshToken rotated via httpOnly cookie
}

// ─── Integration Types ──────────────────────────────────────────────────────

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
}

export interface ApiErrorResponse {
  success: false;
  error: ApiErrorPayload;
}

export type StoredAssetCategory =
  | 'avatar'
  | 'review_export'
  | 'review_attachment';

export type StoredAssetAccessScope = 'public' | 'private';

export interface StoredAssetSummary {
  assetId: string;
  category: StoredAssetCategory;
  accessScope: StoredAssetAccessScope;
  originalFileName?: string | null;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export type IntegrationProvider = 'notion' | 'slack' | 'dropbox' | 'google_drive' | 'gmail' | 'linear';
export type OrchestrationIntegrationKey = IntegrationProvider | 'corner_internal';
export type IntegrationProviderId = IntegrationProvider;

export type IntegrationCategory =
  | 'source'
  | 'destination'
  | 'context'
  | 'system_of_record'
  | 'communication'
  | 'storage'
  | 'work_management';

export type IntegrationAction =
  | 'search'
  | 'list'
  | 'read_metadata'
  | 'read_content'
  | 'import'
  | 'export'
  | 'upload'
  | 'create'
  | 'update'
  | 'send'
  | 'post'
  | 'move'
  | 'archive';

export type IntegrationObjectType =
  | 'file'
  | 'folder'
  | 'document'
  | 'page'
  | 'message'
  | 'channel'
  | 'database_record'
  | 'issue'
  | 'project'
  | 'comment'
  | 'team';

export interface IntegrationPrerequisites {
  authenticatedUser: boolean;
  connected: boolean;
}

export interface IntegrationCapabilitySummary {
  key: OrchestrationIntegrationKey;
  label: string;
  categories: IntegrationCategory[];
  supportedActions: IntegrationAction[];
  supportedObjectTypes: IntegrationObjectType[];
  canBeSystemOfRecord: boolean;
  supportsWrites: boolean;
  safeForPassiveContext: boolean;
  requiresConfirmationFor: Array<'write' | 'external_delivery' | 'destructive'>;
  prerequisites: IntegrationPrerequisites;
  limitations: string[];
}

export type ProviderAction =
  | 'search_items'
  | 'fetch_item'
  | 'list_children'
  | 'materialize_item_to_text'
  | 'materialize_item_to_binary'
  | 'create_item'
  | 'append_comment'
  | 'update_item'
  | 'send_message'
  | 'upload_file'
  | 'export_item';

export interface SearchItemsActionInput {
  action: 'search_items';
  query: string;
  cursor?: string;
  limit?: number;
  parentId?: string;
  filters?: Record<string, unknown>;
}

export interface FetchItemActionInput {
  action: 'fetch_item';
  itemId: string;
}

export interface ListChildrenActionInput {
  action: 'list_children';
  parentId?: string;
  cursor?: string;
  limit?: number;
  query?: string;
  filters?: Record<string, unknown>;
}

export interface MaterializeItemToTextActionInput {
  action: 'materialize_item_to_text';
  itemId: string;
  format?: string;
}

export interface MaterializeItemToBinaryActionInput {
  action: 'materialize_item_to_binary';
  itemId: string;
  format?: string;
}

export interface CreateItemActionInput {
  action: 'create_item';
  payload: Record<string, unknown>;
  parentId?: string;
}

export interface AppendCommentActionInput {
  action: 'append_comment';
  itemId: string;
  body: string;
}

export interface UpdateItemActionInput {
  action: 'update_item';
  itemId: string;
  payload: Record<string, unknown>;
}

export interface SendMessageActionInput {
  action: 'send_message';
  message: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    text?: string;
    html?: string;
  };
  attachments?: Array<{
    fileId?: string;
    documentId?: string;
    exportId?: string;
    fileName?: string;
    mimeType?: string;
  }>;
}

export interface UploadFileActionInput {
  action: 'upload_file';
  payload: Record<string, unknown>;
  destination?: {
    parentId?: string;
    path?: string;
  };
}

export interface ExportItemActionInput {
  action: 'export_item';
  itemId?: string;
  payload: Record<string, unknown>;
  format?: string;
  destination?: {
    parentId?: string;
    path?: string;
  };
}

export type ProviderActionInput =
  | SearchItemsActionInput
  | FetchItemActionInput
  | ListChildrenActionInput
  | MaterializeItemToTextActionInput
  | MaterializeItemToBinaryActionInput
  | CreateItemActionInput
  | AppendCommentActionInput
  | UpdateItemActionInput
  | SendMessageActionInput
  | UploadFileActionInput
  | ExportItemActionInput;

export interface NormalizedSearchItem {
  provider: IntegrationProviderId;
  itemType: IntegrationObjectType | string;
  id: string;
  title: string;
  subtitle?: string | null;
  url?: string | null;
  status?: string | null;
  teamName?: string | null;
  updatedAt?: string | null;
  rawRef?: string | null;
}

export interface NormalizedIssue {
  provider: IntegrationProviderId;
  itemType: 'issue';
  id: string;
  identifier: string;
  title: string;
  description?: string | null;
  status?: string | null;
  team?: string | null;
  assignee?: string | null;
  project?: string | null;
  url: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  rawRef?: string | null;
}

export interface NormalizedComment {
  provider: IntegrationProviderId;
  itemType: 'comment';
  id: string;
  body: string;
  author?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  rawRef?: string | null;
}

export interface CanonicalFetchedItem {
  id: string;
  name: string;
  mimeType: string | null;
  size?: number | null;
  createdAt?: string | null;
  modifiedAt?: string | null;
  downloadUrl?: string | null;
  webUrl?: string | null;
  providerMetadata?: Record<string, unknown>;
}

export interface ProviderObservation {
  ok: boolean;
  provider: IntegrationProviderId;
  action: ProviderAction;
  retryable: boolean;
  authFailure: boolean;
  rateLimited: boolean;
  statusCode?: number;
  warnings?: string[];
  normalizedData?: unknown;
  rawRef?: string;
  providerMetrics?: ProviderExecutionMetric;
}

export interface ProviderExecutionMetric {
  provider: IntegrationProviderId;
  action: ProviderAction;
  latencyMs: number;
  attempts: number;
  timedOut: boolean;
  success: boolean;
  errorCode?: string | null;
  executedAt: string;
  /** User-initiated retry attempt number (0 = first attempt). */
  nodeRetryAttempt?: number;
  /** Task node ID this execution belongs to. */
  taskId?: string;
}

export interface IndexingMetric {
  sourceId: string;
  indexingDurationMs: number;
  ocrInvoked: boolean;
  chunkCount: number;
  embeddingAttempts: number;
}

/** Token usage from a single model invocation. */
export interface ModelCallUsage {
  model: string;
  role: 'supervisor' | 'planner' | 'executor' | 'synthesis' | 'embedding';
  inputTokens: number;
  outputTokens: number;
  /** Task node this call was made for (null for run-level calls like supervisor/synthesis). */
  taskId?: string | null;
  /** Retry attempt number (0 = first attempt). Only > 0 during retried executions. */
  retryAttempt?: number;
}

/** Aggregated token usage across a run, broken down by role. */
export interface ModelTokenUsage {
  totalInputTokens: number;
  totalOutputTokens: number;
  /** Per-role breakdown. */
  byRole: Record<string, { inputTokens: number; outputTokens: number }>;
  /** Individual call records for auditability. */
  calls: ModelCallUsage[];
}

export interface ExecutionPolicy {
  timeoutMs: number;
  maxRetries: number;
}

export interface IdempotencyPolicy {
  required: boolean;
  key?: string;
  ttlSeconds: number;
}

export interface RateLimitPolicy {
  strategy: 'none' | 'retry_after' | 'exponential_backoff';
  maxRetries: number;
  baseDelayMs: number;
}

export interface IntegrationCapability {
  provider: IntegrationProviderId;
  action: ProviderAction;
  objectTypes: IntegrationObjectType[];
  write: boolean;
  retryable: boolean;
  timeoutMs: number;
  paginated: boolean;
  idempotent: boolean;
  requiresIdempotency: boolean;
}

export interface ProviderAdapterContext {
  userId: string;
  runId?: string;
  taskId?: string;
  stepId?: string;
  idempotencyKey?: string;
  connection?: {
    id: string;
    provider: IntegrationProviderId;
    status: IntegrationConnectionStatus;
    workspaceId?: string;
    workspaceName?: string | null;
    workspaceIcon?: string | null;
  } | null;
}

export interface ProviderAdapter {
  provider: IntegrationProviderId;
  getCapabilities(): IntegrationCapability[];
  getConnectionStatus(userId?: string): Promise<IntegrationConnectionSummary>;
  execute(
    action: ProviderAction,
    input: ProviderActionInput,
    context: ProviderAdapterContext,
  ): Promise<ProviderObservation>;
}

export interface ExecutionGateway {
  execute(input: {
    provider: IntegrationProviderId;
    action: ProviderAction;
    input: ProviderActionInput;
    userId: string;
    workspaceId?: string;
    runId?: string;
    taskId?: string;
    stepId?: string;
    attempt?: number;
    idempotencyKey?: string;
  }): Promise<ProviderObservation>;
}

export interface IntegrationConnectionSummary {
  key: OrchestrationIntegrationKey;
  connected: boolean;
  status: string;
  label: string;
}

export interface IntegrationPreferences {
  preferredContextSources: IntegrationProvider[];
  preferredSystemOfRecord: IntegrationProvider | null;
  preferredImportSources: IntegrationProvider[];
  preferredDeliveryDestinations: IntegrationProvider[];
  autoUseContextSources: boolean;
  requireConfirmationForExternalActions: boolean;
}

export type IntegrationConnectionStatus =
  | 'active'
  | 'revoked'
  | 'error'
  | 'disconnected';

export type NotionConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface NotionConnectionStatusData {
  connected: boolean;
  status: NotionConnectionStatus;
  workspaceId?: string;
  workspaceName?: string | null;
  workspaceIcon?: string | null;
  botId?: string;
  connectedAt?: string;
  updatedAt?: string;
  lastSyncedAt?: string | null;
}

export interface NotionAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
}

export interface NotionTargetParent {
  type: string;
  id?: string;
}

export type NotionTargetType = 'page' | 'database';

export interface NotionTarget {
  id: string;
  title: string;
  type: NotionTargetType;
  url: string;
  parent: NotionTargetParent | null;
  lastEditedTime?: string;
  iconEmoji?: string;
  iconUrl?: string | null;
}

export interface NotionTargetsData {
  results: NotionTarget[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface NotionPropertyOption {
  id?: string;
  name: string;
  color?: string | null;
}

export interface NotionDatabasePropertySchema {
  id: string;
  name: string;
  type: string;
  readOnly: boolean;
  required?: boolean;
  description?: string | null;
  options?: NotionPropertyOption[];
  relationDatabaseId?: string | null;
}

export interface NotionDatabaseSchemaData {
  id: string;
  title: string;
  url: string;
  properties: NotionDatabasePropertySchema[];
  lastEditedTime?: string;
}

export interface NotionDatabaseEntryPropertyValue {
  name: string;
  type: string;
  readOnly: boolean;
  value: unknown;
  displayText?: string | null;
  raw: Record<string, unknown>;
}

export type NotionBlockInput = Record<string, unknown>;

export interface NotionCreatePageRequest {
  parentPageId?: string;
  title: string;
  children?: NotionBlockInput[];
  clientReferenceId?: string;
  icon?: Record<string, unknown>;
  cover?: Record<string, unknown>;
}

export interface NotionPageSummary {
  id: string;
  title: string;
  url: string;
  type: 'page';
  parent: NotionTargetParent | null;
  lastEditedTime?: string;
  clientReferenceId?: string;
}

export interface NotionCreateDatabaseEntryRequest {
  databaseId: string;
  properties: Record<string, unknown>;
  children?: NotionBlockInput[];
  clientReferenceId?: string;
  icon?: Record<string, unknown>;
  cover?: Record<string, unknown>;
}

export interface NotionDatabaseEntrySummary {
  id: string;
  title: string;
  url: string;
  type: 'database_entry';
  parent: NotionTargetParent | null;
  lastEditedTime?: string;
  databaseId?: string;
  clientReferenceId?: string;
}

export interface NotionDatabaseEntryData extends NotionDatabaseEntrySummary {
  properties: Record<string, NotionDatabaseEntryPropertyValue>;
}

export interface NotionUpdateDatabaseEntryRequest {
  properties?: Record<string, unknown>;
  appendChildren?: NotionBlockInput[];
  icon?: Record<string, unknown>;
  cover?: Record<string, unknown>;
  inTrash?: boolean;
}

export interface NotionDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export type SlackConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface SlackAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
}

export interface SlackConnectionStatusData {
  connected: boolean;
  status: SlackConnectionStatus;
  teamId?: string;
  teamName?: string | null;
  teamDomain?: string | null;
  appId?: string | null;
  connectedAt?: string;
  updatedAt?: string;
}

export interface SlackDocumentProvenance {
  teamId: string;
  teamName?: string | null;
  channelId?: string | null;
  messageTs?: string | null;
  threadTs?: string | null;
  uploaderUserId?: string | null;
  slackPermalink?: string | null;
}

export type ImportedDocumentSourceSystem = 'slack' | 'dropbox' | 'google_drive';

export type ImportedDocumentKind =
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'text'
  | 'image_scan'
  | 'unknown';

export type SlackDocumentKind = ImportedDocumentKind;

export interface SlackDocumentCandidate {
  id: string;
  title: string;
  mimeType: string;
  extension?: string | null;
  sizeBytes: number;
  sourceCreatedAt?: string | null;
  previewUrl?: string | null;
  downloadUrl?: string | null;
  documentKind: SlackDocumentKind;
  isSignable: boolean;
  isScanned: boolean;
  isTemplate: boolean;
  provenance: SlackDocumentProvenance;
}

export interface SlackDocumentsData {
  results: SlackDocumentCandidate[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ImportedDocumentSummary {
  documentId: string;
  sourceSystem: ImportedDocumentSourceSystem;
  sourceType: 'file';
  sourceDocumentId: string;
  title: string;
  originalFileName: string;
  mimeType: string;
  extension?: string | null;
  sizeBytes: number;
  downloadUrl: string;
  documentKind: SlackDocumentKind;
  isSignable: boolean;
  isScanned: boolean;
  isTemplate: boolean;
  provenance: ImportedDocumentProvenance;
  createdAt: string;
  updatedAt: string;
}

export interface SlackImportFailure {
  fileId: string;
  code: string;
  message: string;
}

export interface SlackImportResultData {
  imported: ImportedDocumentSummary[];
  failed: SlackImportFailure[];
}

export interface SlackDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export interface SlackChannelInfo {
  id: string;
  name: string;
  memberCount: number;
  topic?: string;
  isPrivate: boolean;
}

export interface SlackChannelsData {
  channels: SlackChannelInfo[];
  nextCursor?: string;
}

export interface SlackSendMessageData {
  ok: true;
  channelId: string;
  timestamp: string;
}

export type DropboxConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface DropboxAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
}

export interface DropboxConnectionStatusData {
  connected: boolean;
  status: DropboxConnectionStatus;
  accountId?: string;
  teamId?: string | null;
  displayName?: string | null;
  email?: string | null;
  profilePhotoUrl?: string | null;
  connectedAt?: string;
  updatedAt?: string;
}

export interface DropboxDocumentProvenance {
  accountId: string;
  teamId?: string | null;
  path: string;
  pathLower?: string | null;
  sharedLink?: string | null;
  contentHash?: string | null;
  rev?: string | null;
}

export type DropboxDocumentKind = ImportedDocumentKind;

export interface GoogleDriveDocumentProvenance {
  accountId: string;
  parentIds?: string[] | null;
  webViewLink?: string | null;
  iconLink?: string | null;
  originalMimeType: string;
  driveFileName: string;
  modifiedTime?: string | null;
}

export type ImportedDocumentProvenance =
  | SlackDocumentProvenance
  | DropboxDocumentProvenance
  | GoogleDriveDocumentProvenance;

export interface DropboxDocumentCandidate {
  id: string;
  title: string;
  mimeType: string;
  extension?: string | null;
  sizeBytes: number;
  sourceCreatedAt?: string | null;
  sourceModifiedAt?: string | null;
  pathDisplay: string;
  pathLower?: string | null;
  previewUrl?: string | null;
  downloadReference: string;
  documentKind: DropboxDocumentKind;
  isSignable: boolean;
  isScanned: boolean;
  isTemplate: boolean;
  provenance: DropboxDocumentProvenance;
}

export interface DropboxDocumentsData {
  results: DropboxDocumentCandidate[];
  cursor: string | null;
  hasMore: boolean;
  path: string;
}

export interface DropboxImportFailure {
  reference: string;
  code: string;
  message: string;
}

export interface DropboxImportResultData {
  imported: ImportedDocumentSummary[];
  failed: DropboxImportFailure[];
}

export interface DropboxImportRequest {
  fileIds?: string[];
  paths?: string[];
}

export interface DropboxExportRequest {
  documentId?: string;
  fileId?: string;
  destinationPath?: string;
  fileName?: string;
  createSharedLink?: boolean;
  overwrite?: boolean;
}

export interface DropboxExportResultData {
  id: string;
  name: string;
  pathDisplay: string;
  pathLower?: string | null;
  sizeBytes?: number | null;
  clientModified?: string | null;
  serverModified?: string | null;
  sharedLink?: string | null;
}

export interface DropboxDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export type GoogleDriveConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface GoogleDriveAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
  scopes: string[];
}

export interface GoogleDriveConnectionStatusData {
  connected: boolean;
  status: GoogleDriveConnectionStatus;
  accountId?: string;
  displayName?: string | null;
  email?: string | null;
  photoUrl?: string | null;
  scopes?: string[];
  connectedAt?: string;
  updatedAt?: string;
}

export type GoogleDriveDownloadMode = 'binary' | 'export';

export interface GoogleDriveFileSummary {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string | null;
  sizeBytes?: number | null;
  webViewLink?: string | null;
  iconLink?: string | null;
  parents: string[];
  isFolder: boolean;
  provider: 'google_drive';
  importSupported: boolean;
}

export interface GoogleDriveFilesData {
  results: GoogleDriveFileSummary[];
  nextPageToken: string | null;
  hasMore: boolean;
}

export interface GoogleDriveFileMetadataData extends GoogleDriveFileSummary {
  isGoogleWorkspaceFile: boolean;
  downloadMode: GoogleDriveDownloadMode | null;
}

export interface GoogleDriveImportRequest {
  fileIds: string[];
}

export interface GoogleDriveImportFailure {
  fileId: string;
  code: string;
  message: string;
}

export interface GoogleDriveImportResultData {
  imported: ImportedDocumentSummary[];
  failed: GoogleDriveImportFailure[];
}

export interface GoogleDriveExportRequest {
  documentId?: string;
  fileId?: string;
  folderId?: string;
  fileName?: string;
}

export interface GoogleDriveExportResultData {
  id: string;
  name: string;
  webViewLink: string | null;
  folderId: string;
}

export interface GoogleDriveDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export type GmailConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface GmailAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
  scopes: string[];
}

export interface GmailConnectionStatusData {
  connected: boolean;
  status: GmailConnectionStatus;
  accountId?: string | null;
  email?: string | null;
  scopes?: string[];
  connectedAt?: string;
  updatedAt?: string;
}

export type GmailAttachmentSource = 'file' | 'document';

export interface GmailSendRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  textBody: string;
  htmlBody?: string;
  fileIds?: string[];
  documentIds?: string[];
}

export interface GmailAttachmentSummary {
  source: GmailAttachmentSource;
  referenceId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface GmailSendResultData {
  sent: true;
  providerMessageId?: string | null;
  threadId?: string | null;
  sentAt: string;
  recipientCount: number;
  attachments: GmailAttachmentSummary[];
}

export interface GmailDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export type LinearConnectionStatus = IntegrationConnectionStatus | 'not_connected';

export interface LinearAuthStartData {
  authorizationUrl: string;
  redirectUri: string;
  scopes: string[];
}

export interface LinearConnectionStatusData {
  connected: boolean;
  status: LinearConnectionStatus;
  workspaceId?: string | null;
  workspaceName?: string | null;
  accountId?: string | null;
  accountEmail?: string | null;
  accountName?: string | null;
  scopes?: string[];
  connectedAt?: string;
  updatedAt?: string;
  lastSyncedAt?: string | null;
}

export interface LinearSearchItemsData {
  results: NormalizedSearchItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface LinearIssueCommentsData {
  results: NormalizedComment[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface LinearTeamSummary {
  id: string;
  name: string;
  key?: string | null;
}

export interface LinearWorkflowStateSummary {
  id: string;
  name: string;
  type?: string | null;
  teamId?: string | null;
  teamName?: string | null;
}

export interface LinearUserSummary {
  id: string;
  name?: string | null;
  email?: string | null;
  active?: boolean;
}

export interface LinearProjectSummary {
  id: string;
  name: string;
  url?: string | null;
  teamIds: string[];
  teamNames: string[];
}

export interface LinearCatalogData {
  teams: LinearTeamSummary[];
  states: LinearWorkflowStateSummary[];
  assignees: LinearUserSummary[];
  projects: LinearProjectSummary[];
}

export interface LinearCreateIssueRequest {
  title: string;
  description?: string;
  teamId: string;
  projectId?: string;
  stateId?: string;
  priority?: number;
  assigneeId?: string;
}

export interface LinearAppendCommentRequest {
  body: string;
}

export interface LinearUpdateIssueRequest {
  title?: string;
  description?: string;
  stateId?: string;
  projectId?: string;
  priority?: number;
  assigneeId?: string;
}

export interface LinearDisconnectData {
  connected: false;
  status: 'not_connected' | 'disconnected';
}

export type ReviewSourceKind = 'file' | 'document';
export type ReviewStatus = 'draft' | 'generating' | 'ready' | 'completed';
export type ReviewSuggestionPriority = 'high' | 'medium' | 'low';
export type ReviewSuggestionStatus = 'pending' | 'accepted' | 'dismissed';
export type ReviewSuggestionCategory = 'rewrite' | 'addition' | 'deletion';
export type ReviewThreadStatus = 'open' | 'resolved';
export type ReviewParticipantRole = 'owner' | 'reviewer';

export interface ReviewSuggestion {
  id: string;
  sectionRef: string;
  sectionTitle: string;
  priority: ReviewSuggestionPriority;
  category: ReviewSuggestionCategory;
  summary: string;
  originalText: string;
  proposedText: string;
  rationale: string;
  tags: string[];
  shortLabel?: string;
  status: ReviewSuggestionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewComment {
  id: string;
  body: string;
  authorName: string;
  authorEmail?: string | null;
  authorRole: ReviewParticipantRole;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewThread {
  id: string;
  reviewId: string;
  suggestionId?: string | null;
  status: ReviewThreadStatus;
  comments: ReviewComment[];
  createdAt: string;
  updatedAt: string;
}

export interface ReviewInvite {
  inviteId: string;
  reviewId: string;
  email: string;
  name: string;
  role: ReviewParticipantRole;
  status: 'pending' | 'viewed' | 'accepted' | 'revoked';
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewParticipant {
  id: string;
  role: ReviewParticipantRole;
  name: string;
  email?: string | null;
  status: 'active' | 'pending' | 'revoked';
}

export interface ReviewStats {
  total: number;
  accepted: number;
  dismissed: number;
  remaining: number;
  highPriority: number;
  rewrites: number;
  additions: number;
  deletions: number;
}

export interface ReviewExport {
  exportId: string;
  reviewId: string;
  asset: StoredAssetSummary;
  fileName: string;
  mimeType: string;
  createdAt: string;
}

export interface Review {
  reviewId: string;
  ownerId: string;
  sourceKind: ReviewSourceKind;
  sourceId: string;
  title: string;
  status: ReviewStatus;
  suggestions: ReviewSuggestion[];
  threads: ReviewThread[];
  invites: ReviewInvite[];
  participants: ReviewParticipant[];
  stats: ReviewStats;
  lastGeneratedAt?: string | null;
  latestExport?: ReviewExport | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReviewRequest {
  sourceKind: ReviewSourceKind;
  sourceId: string;
  title?: string;
}

export interface ReviewSuggestionPatchRequest {
  status: ReviewSuggestionStatus;
}

export interface CreateReviewThreadRequest {
  suggestionId?: string;
  body: string;
}

export interface CreateReviewCommentRequest {
  body: string;
}

export interface CreateReviewInviteRequest {
  email: string;
  name: string;
}

// ─── Agentic Query Engine Types ─────────────────────────────────────────────

export type ImportedDocumentExtractionStatus =
  | 'complete'
  | 'pending'
  | 'failed'
  | 'pointer_only';

export type QuerySourceKind = 'file' | 'document';

export interface QuerySourceRef {
  kind: QuerySourceKind;
  id: string;
  name: string;
  mimeType: string;
}

export interface SupervisorTaskNode {
  id: string;
  description: string;
  dependsOn: string[];
  estimatedTools: string[];
  status: 'pending';
}

export interface SupervisorOutput {
  taskGraph: SupervisorTaskNode[];
  queryIntent: string;
  requiredDocuments: string[];
}

export type ReasoningSegmentType = 'STRATEGY' | 'OBSERVATION' | 'DECISION' | 'WARNING';

export interface ReasoningSegment {
  type: ReasoningSegmentType;
  text: string;
  taskId: string;
  at: string;
}

export type QueryEngineToolName =
  | 'keyword_search'
  | 'search_chunks'
  | 'fetch_chunk_window'
  | 'extract_fact_from_evidence'
  | 'synthesize_answer'
  | 'provider_action';

export interface PlannerExecutionStep {
  stepId: string;
  instruction: string;
  toolToCall: QueryEngineToolName;
  provider?: IntegrationProviderId;
  providerAction?: ProviderAction;
  args: Record<string, unknown>;
  expectedOutput: string;
  fallbackStrategy: string;
}

export interface PlannerOutput {
  taskId: string;
  thinkingTrace: string;
  executionSteps: PlannerExecutionStep[];
}

export interface Citation {
  citationId: string;
  documentId: string;
  documentName: string;
  chunkId: string;
  indexId: string;
  contentHash: string;
  chunkTokenOffset: {
    start: number;
    end: number;
  } | null;
  excerpt: string;
  pageNumber?: number | null;
  sectionHeading?: string | null;
  confidenceScore: number;
  taskId: string;
}

export type TaskNodeStatus =
  | 'pending'
  | 'running'
  | 'replanning'
  | 'completed'
  | 'failed'
  | 'blocked'
  | 'cancelled'
  | 'retry_pending'
  | 'skipped';

export type RunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'partial'
  | 'cancelled';

export interface AgentTaskNode {
  id: string;
  label: string;
  status: TaskNodeStatus;
  dependsOn: string[];
  branchId: string;
  estimatedTools: string[];
  visibleSteps: string[];
  result?: string | null;
  error?: string | null;
  citations?: Citation[];
  startedAt?: string | null;
  completedAt?: string | null;
  /** Whether this node can be retried by the user (Phase A: read-only nodes only) */
  retryable?: boolean;
  /** Whether this node can be skipped by the user */
  skippable?: boolean;
  /** Number of user-initiated retries performed */
  retryCount?: number;
  /** Maximum allowed user retries (default 3) */
  maxRetries?: number;
  /** ISO timestamp of last user-initiated retry request */
  lastRetryAt?: string | null;
}

export interface ExecutorStepResult {
  stepId: string;
  toolCalled: QueryEngineToolName;
  provider?: IntegrationProviderId;
  providerAction?: ProviderAction;
  argsUsed: Record<string, unknown>;
  rawObservation: string;
  interpretation: string;
  status: 'completed' | 'failed' | 'replanning';
  checklistLabel: string;
  citations?: Citation[];
  providerMetrics?: ProviderExecutionMetric;
}

export interface AgentRunMetrics {
  taskCount: number;
  citationCount: number;
  supervisorLatencyMs: number | null;
  plannerLatencyMsByTask: Record<string, number>;
  executorLatencyMsByTask: Record<string, number>;
  embeddingLatencyMs: number;
  synthesisLatencyMs: number | null;
  timeToFirstChecklistMs: number | null;
  timeToFirstThinkingMs: number | null;
  timeToFirstAnswerTokenMs: number | null;
  providerExecutions?: ProviderExecutionMetric[];
  indexingMetrics?: IndexingMetric[];
  replanCountByTask?: Record<string, number>;
  totalReplanCount?: number;
  citedTaskCount?: number;
  uncitedTaskCount?: number;
  citationCoverage?: number | null;
  totalTokensUsed?: number | null;
  estimatedCostUsd?: number | null;
  tokenUsageEstimated?: boolean;
  /** Detailed token usage broken down by model role. */
  tokenUsage?: ModelTokenUsage;
  /** Token usage attributable to retry attempts only. */
  retryTokenUsage?: ModelTokenUsage;
  /** Executor latency attributable to user-initiated retry attempts, per task. */
  retryLatencyMsByTask?: Record<string, number>;
  /** Total executor latency from all retry attempts across the run. */
  totalRetryLatencyMs?: number;
  /** Provider executions that occurred during retry attempts (subset of providerExecutions). */
  retryProviderExecutions?: ProviderExecutionMetric[];
  /** Total number of user-initiated retries in this run. */
  userRetryCount?: number;
  /** Per-task count of user-initiated retries. */
  retryCountByTask?: Record<string, number>;
}

export interface AgentRunSnapshot {
  runId: string;
  version?: number;
  status: RunStatus;
  userId: string;
  query: string;
  queryIntent?: string | null;
  conversationId?: string | null;
  sources: QuerySourceRef[];
  tasks: AgentTaskNode[];
  finalAnswer?: string | null;
  citations: Citation[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  error?: string | null;
  metrics?: AgentRunMetrics;
}

export interface AgentRunRequest {
  query: string;
  fileIds?: string[];
  documentIds?: string[];
  conversationId?: string;
}

export interface AgentRunCreateResponse {
  runId: string;
  streamUrl: string;
  snapshotUrl: string;
}

export type WorkflowPresetId =
  | 'contract_send'
  | 'contract_review'
  | 'lease_agreement_review'
  | 'employee_agreement_review'
  | 'extract_obligations_and_deadlines'
  | 'draft_client_summary'
  | 'draft_client_alert'
  | 'generate_post_closing_timeline'
  | 'compliance_notice_summary';

export type WorkflowDefinitionId = WorkflowPresetId | (string & {});

export type WorkflowCategory =
  | 'review'
  | 'extraction'
  | 'drafting'
  | 'timeline'
  | 'compliance'
  | 'send'
  | 'general';

export type WorkflowExecutionMode =
  | 'manual_assist'
  | 'semi_automated'
  | 'automation_ready';

export type WorkflowStatus =
  | 'draft'
  | 'active'
  | 'archived';

export type WorkflowTriggerType =
  | 'manual'
  | 'on_document_import'
  | 'on_email_received'
  | 'on_folder_sync'
  | 'scheduled';

export type WorkflowSupportedDocumentType =
  | 'contract'
  | 'lease'
  | 'employment_agreement'
  | 'notice'
  | 'closing_set'
  | 'general_document';

export type WorkflowRecommendedSource =
  | 'local_upload'
  | 'google_drive'
  | 'dropbox'
  | 'gmail'
  | 'slack'
  | 'notion';

export type WorkflowVariableType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'string_array'
  | 'object'
  | 'citation_list';

export interface WorkflowUserInputField {
  key: string;
  label: string;
  type: 'string' | 'textarea' | 'select' | 'date' | 'boolean';
  description?: string;
  required: boolean;
  options?: string[];
}

export type WorkflowNormalizedAction =
  | 'classify_document'
  | 'retrieve_relevant_sections'
  | 'extract_key_terms'
  | 'extract_obligations'
  | 'identify_dates'
  | 'identify_risks'
  | 'compare_against_standard'
  | 'draft_summary'
  | 'draft_alert'
  | 'build_timeline'
  | 'prepare_export_payload';

export interface WorkflowOutputField {
  key: string;
  label: string;
  type: WorkflowVariableType;
  description: string;
  required: boolean;
  children?: WorkflowOutputField[];
}

export interface WorkflowOutputSchema {
  fields: WorkflowOutputField[];
}

export interface WorkflowExportHints {
  preferredFormats: string[];
  defaultArtifactLabel?: string;
  includeCitationsByDefault: boolean;
}

export type WorkflowNodeType =
  | 'document_input'
  | 'source_input'
  | 'user_selection'
  | 'condition'
  | 'classify_task'
  | 'extraction_task'
  | 'review_task'
  | 'drafting_task'
  | 'approval_gate'
  | 'export_action'
  | 'notification_action'
  | 'response'
  // ── Contract velocity nodes (act on real PDFs/signing requests) ──
  | 'clone_document'
  | 'fill_placeholders'
  | 'apply_signature'
  | 'dispatch_for_signature';

export type WorkflowConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'in'
  | 'not_in'
  | 'exists'
  | 'not_exists'
  | 'greater_than'
  | 'less_than';

export interface WorkflowNodePosition {
  x: number;
  y: number;
}

export interface WorkflowNodeBinding {
  sourceKey: string;
  targetKey: string;
}

export interface WorkflowEdgeCondition {
  variableKey: string;
  operator: WorkflowConditionOperator;
  value?: string | number | boolean | string[];
}

export interface WorkflowEdge {
  edgeId: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
  condition?: WorkflowEdgeCondition | null;
}

export interface WorkflowNodeExecutionPolicy {
  retryable?: boolean;
  timeoutMs?: number;
  requiresApproval?: boolean;
  allowManualIntervention?: boolean;
}

export interface DocumentInputNodeConfig {
  acceptedDocumentTypes: WorkflowSupportedDocumentType[];
  allowMultiple: boolean;
  required: boolean;
  outputKey: string;
}

export interface SourceInputNodeConfig {
  allowedSources: WorkflowRecommendedSource[];
  required: boolean;
  outputKey: string;
}

export interface UserSelectionNodeConfig {
  variableKey: string;
  promptLabel: string;
  selectionType: 'single_select' | 'multi_select' | 'boolean';
  options: string[];
  required: boolean;
}

export interface ConditionNodeConfig {
  variableKey: string;
  description?: string;
  defaultTargetBehavior: 'fail' | 'continue' | 'stop';
}

export interface TaskNodeConfig {
  goal: string;
  normalizedAction: WorkflowNormalizedAction;
  promptTemplate?: string;
  inputKeys: string[];
  outputKey: string;
  referenceFileContent?: string;
  referenceFileName?: string;
}

export interface ApprovalGateNodeConfig {
  approvalKey: string;
  approverRole?: string;
  required: boolean;
}

export interface ExportActionNodeConfig {
  formats: string[];
  destinationHints?: string[];
  outputKey?: string;
}

export interface NotificationActionNodeConfig {
  channelHints?: string[];
  messageTemplate?: string;
}

export interface ResponseNodeConfig {
  responseKey: string;
  includeCitationsByDefault?: boolean;
}

// ── Contract velocity node configs ──────────────────────────────────
//
// These four nodes turn a successful contract send (capture-from-chat) into a
// repeatable one-click workflow. Geometry / detection / signing logic stays
// inside the existing tools (fillPdfForm, signDocumentTool, signingService);
// the workflow runner just resolves variables and calls them server-side.

export interface CloneDocumentNodeConfig {
  /** ProcessedFile fileId of the master template captured from a chat send. */
  sourceFileId: string;
  /** Variable key the cloned fileId is written under (downstream nodes read it). */
  outputKey: string;
}

/**
 * Captured at workflow-creation time: the values the chat originally filled
 * into placeholder fields, plus a hint of where they came from. At run time
 * we replay these as baseline, then overlay any deltas the user supplies via
 * `initialVariables`.
 */
export interface CapturedPlaceholderValue {
  /** DetectedDocField id the chat targeted. */
  fieldId: string;
  /** Human label (e.g. "Buyer name", "Closing date"). */
  label?: string;
  /** Field role (buyer_name, agreement_date, …). */
  role?: string;
  /** Value the chat wrote into the field. */
  value: string;
  /** Variable key in `initialVariables` whose presence overrides this baseline. */
  deltaKey?: string;
  /** Why the value is captured: profile-derived, current-date, AI-suggested, user-typed. */
  source?: 'profile' | 'date' | 'ai' | 'user';
  /** When true, the HUD must surface this field as an editable delta. */
  isDelta?: boolean;
}

export interface FillPlaceholdersNodeConfig {
  /** Variable key holding the working PDF fileId (produced by clone_document). */
  documentKey: string;
  /** Baseline values captured from the original chat send. */
  baselineValues: CapturedPlaceholderValue[];
  /** Delta variable keys the runner must look up in collectedVariables. */
  deltaKeys: string[];
  /** Variable key the post-fill fileId is written under. */
  outputKey: string;
  /** When true, fields whose source === 'date' are recomputed to today on every run. */
  recomputeDates?: boolean;
  /** Optional signing-role filter forwarded to fillPdfForm. */
  signingRole?: string;
}

export interface ApplySignatureNodeConfig {
  /** Variable key holding the working PDF fileId. */
  documentKey: string;
  /** Party role to sign as (e.g. "buyer", "agent", "client"). */
  signerRole: string;
  /** Variable key the post-signature fileId is written under. */
  outputKey: string;
  /** Defaults to 'signature'. */
  fieldType?: 'signature' | 'initials' | 'date';
}

export interface DispatchRecipientShape {
  /** Stable identifier within this workflow (e.g. "primary", "buyer"). */
  recipientId: string;
  /** Default role label sent to the signer ("buyer", "client"…). */
  role: string;
  /** Variable key in `initialVariables` whose value is the recipient email. */
  emailKey: string;
  /** Variable key whose value is the recipient name. */
  nameKey: string;
}

export interface DispatchForSignatureNodeConfig {
  /** Variable key holding the PDF to dispatch. */
  documentKey: string;
  /** Recipients the runner resolves from initialVariables. */
  recipients: DispatchRecipientShape[];
  /** Variable key the resulting signingRequestId is written under. */
  outputKey: string;
  /** Optional message sent with the invitation. */
  message?: string;
  /** Optional expiry window (days). */
  expiresInDays?: number;
}

export interface WorkflowNodeBase<TType extends WorkflowNodeType, TConfig> {
  nodeId: string;
  type: TType;
  label: string;
  description?: string;
  position?: WorkflowNodePosition;
  inputBindings?: WorkflowNodeBinding[];
  outputBindings?: WorkflowNodeBinding[];
  executionPolicy?: WorkflowNodeExecutionPolicy;
  config: TConfig;
}

export type DocumentInputWorkflowNode = WorkflowNodeBase<'document_input', DocumentInputNodeConfig>;
export type SourceInputWorkflowNode = WorkflowNodeBase<'source_input', SourceInputNodeConfig>;
export type UserSelectionWorkflowNode = WorkflowNodeBase<'user_selection', UserSelectionNodeConfig>;
export type ConditionWorkflowNode = WorkflowNodeBase<'condition', ConditionNodeConfig>;
export type ClassifyTaskWorkflowNode = WorkflowNodeBase<'classify_task', TaskNodeConfig>;
export type ExtractionTaskWorkflowNode = WorkflowNodeBase<'extraction_task', TaskNodeConfig>;
export type ReviewTaskWorkflowNode = WorkflowNodeBase<'review_task', TaskNodeConfig>;
export type DraftingTaskWorkflowNode = WorkflowNodeBase<'drafting_task', TaskNodeConfig>;
export type ApprovalGateWorkflowNode = WorkflowNodeBase<'approval_gate', ApprovalGateNodeConfig>;
export type ExportActionWorkflowNode = WorkflowNodeBase<'export_action', ExportActionNodeConfig>;
export type NotificationActionWorkflowNode = WorkflowNodeBase<'notification_action', NotificationActionNodeConfig>;
export type ResponseWorkflowNode = WorkflowNodeBase<'response', ResponseNodeConfig>;
export type CloneDocumentWorkflowNode = WorkflowNodeBase<'clone_document', CloneDocumentNodeConfig>;
export type FillPlaceholdersWorkflowNode = WorkflowNodeBase<'fill_placeholders', FillPlaceholdersNodeConfig>;
export type ApplySignatureWorkflowNode = WorkflowNodeBase<'apply_signature', ApplySignatureNodeConfig>;
export type DispatchForSignatureWorkflowNode = WorkflowNodeBase<'dispatch_for_signature', DispatchForSignatureNodeConfig>;

export type WorkflowNode =
  | DocumentInputWorkflowNode
  | SourceInputWorkflowNode
  | UserSelectionWorkflowNode
  | ConditionWorkflowNode
  | ClassifyTaskWorkflowNode
  | ExtractionTaskWorkflowNode
  | ReviewTaskWorkflowNode
  | DraftingTaskWorkflowNode
  | ApprovalGateWorkflowNode
  | ExportActionWorkflowNode
  | NotificationActionWorkflowNode
  | ResponseWorkflowNode
  | CloneDocumentWorkflowNode
  | FillPlaceholdersWorkflowNode
  | ApplySignatureWorkflowNode
  | DispatchForSignatureWorkflowNode;

export interface WorkflowGraphDefinition {
  entryNodeId: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface WorkflowDefinition {
  workflowId: WorkflowDefinitionId;
  version: number;
  label: string;
  shortDescription: string;
  longDescription: string;
  category: WorkflowCategory;
  tags: string[];
  status: WorkflowStatus;
  triggerType: WorkflowTriggerType;
  executionMode: WorkflowExecutionMode;
  approvalRequired: boolean;
  requiresDocument: boolean;
  minDocuments?: number;
  maxDocuments?: number;
  allowsMultipleDocuments?: boolean;
  supportedDocuments: WorkflowSupportedDocumentType[];
  recommendedSources: WorkflowRecommendedSource[];
  suggestedUserInputs?: WorkflowUserInputField[];
  exportHints: WorkflowExportHints;
  outputSchema: WorkflowOutputSchema;
  graph: WorkflowGraphDefinition;
}

export interface WorkflowGraphValidationIssue {
  code: string;
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export interface WorkflowGraphValidationError extends WorkflowGraphValidationIssue {}

export interface WorkflowGraphValidationWarning extends WorkflowGraphValidationIssue {}

export interface WorkflowGraphValidationResult {
  valid: boolean;
  errors: WorkflowGraphValidationError[];
  warnings: WorkflowGraphValidationWarning[];
}

export type WorkflowBlueprintStepStatus = 'pending';

export interface WorkflowBlueprintStep {
  stepId: string;
  nodeId: string;
  label: string;
  type: WorkflowNodeType;
  purpose?: string;
  normalizedAction?: WorkflowNormalizedAction;
  inputKeys?: string[];
  outputKey?: string;
  approvalGate?: boolean;
  sequence: number;
  status: WorkflowBlueprintStepStatus;
}

export interface WorkflowExecutionBlueprint {
  workflowId: WorkflowDefinitionId;
  version: number;
  label: string;
  category: WorkflowCategory;
  executionMode: WorkflowExecutionMode;
  triggerType: WorkflowTriggerType;
  approvalRequired: boolean;
  outputSchema: WorkflowOutputSchema;
  exportHints: WorkflowExportHints;
  steps: WorkflowBlueprintStep[];
  collectedVariables: Record<string, unknown>;
  entryNodeId: string;
}

export interface WorkflowPresetValidationResult {
  valid: boolean;
  errors: string[];
}

export interface WorkflowPresetRunRequestInput {
  documentCount?: number;
  params?: Record<string, unknown>;
}

export interface WorkflowPresetListResponse {
  presets: WorkflowDefinition[];
}

export interface WorkflowPresetResponse {
  preset: WorkflowDefinition;
}

export interface WorkflowPresetRunRequestResponse {
  preset: WorkflowDefinition;
  validation: WorkflowPresetValidationResult;
  runRequest: WorkflowExecutionBlueprint;
}

export type WorkflowPreset = WorkflowDefinition;
export type WorkflowRunStep = WorkflowBlueprintStep;
export type WorkflowRunRequest = WorkflowExecutionBlueprint;

export type AgentEvent =
  | { type: 'SNAPSHOT_SYNC'; runId: string; snapshotVersion: number; syncedAt: string; internal: true }
  | { type: 'CHECKLIST_INIT'; runId: string; status: 'planning' | 'ready'; query: string; tasks: AgentTaskNode[]; emittedAt: number }
  | { type: 'CHECKLIST_UPDATE'; runId: string; taskId: string; updatedLabel?: string; newSteps?: string[] }
  | { type: 'TASK_STARTED'; runId: string; taskId: string }
  | { type: 'THINKING_SEGMENT'; runId: string; taskId: string; segment: ReasoningSegment }
  | { type: 'STEP_COMPLETE'; runId: string; taskId: string; stepLabel: string; stepResult?: ExecutorStepResult }
  | { type: 'TASK_REPLANNING'; runId: string; taskId: string; reason: string }
  | { type: 'TASK_COMPLETE'; runId: string; taskId: string; result: string; citations?: Citation[] }
  | { type: 'FINAL_ANSWER_TOKEN'; runId: string; token: string }
  | { type: 'FINAL_ANSWER'; runId: string; answer: string; citations: Citation[] }
  | { type: 'RUN_CANCELLED'; runId: string; cancelledAt: string; cancelledBy: string }
  | { type: 'ERROR'; runId: string; message: string; taskId?: string }
  | { type: 'TASK_RETRY_REQUESTED'; runId: string; taskId: string; retryCount: number }
  | { type: 'TASK_SKIPPED'; runId: string; taskId: string };

// ─── Orchestration Types ────────────────────────────────────────────────────

export interface ChefStep {
  toolName: string;
  params: Record<string, unknown>;
  description: string;
  reasoning: string;
  requiresPreviousOutput: boolean;
}

export type IntegrationExecutionOperationKind = 'read_only' | 'mutating' | 'external_delivery';

export type IntegrationExecutionStepKind =
  | 'retrieve_context'
  | 'list_candidates'
  | 'import_into_corner'
  | 'run_corner_tool'
  | 'write_external'
  | 'send_external'
  | 'finalize_result';

export interface IntegrationPlanStep {
  kind: IntegrationExecutionStepKind;
  integrationKey: OrchestrationIntegrationKey;
  action: IntegrationAction;
  description: string;
  objectType?: IntegrationObjectType;
  query?: string;
  itemId?: string;
  itemIds?: string[];
  payload?: Record<string, unknown>;
  requiresConfirmation?: boolean;
}

export interface SystemOfRecordResolution {
  integrationKey: OrchestrationIntegrationKey | null;
  reason:
    | 'explicit_request'
    | 'user_preference'
    | 'default_ranking'
    | 'connected_fallback'
    | 'none';
}

export interface IntegrationExecutionPlan {
  goal: string;
  intent: string;
  operationKind: IntegrationExecutionOperationKind;
  candidateIntegrations: OrchestrationIntegrationKey[];
  connectedIntegrations: IntegrationConnectionSummary[];
  selectedSourceIntegrations: OrchestrationIntegrationKey[];
  selectedContextIntegrations: OrchestrationIntegrationKey[];
  selectedDestinationIntegrations: OrchestrationIntegrationKey[];
  systemOfRecordResolution: SystemOfRecordResolution;
  steps: IntegrationPlanStep[];
  confirmationRequired: boolean;
  failureStrategy: string;
}

export interface ChefPlan {
  understanding: string;
  clarification: string | null;
  confidence: number;
  /** Pre-planned steps (fast-path only). Empty array when using the dynamic executor. */
  steps: ChefStep[];
  integrationPlan?: IntegrationExecutionPlan;
}

export interface FileMetadata {
  name: string;
  type: string;
  size: number;
  index: number;
  /** First ~4KB of extracted document text, for Head Chef intent detection */
  textPreview?: string;
}

/** CORNER_V2: Permission tier for tool calls. Metadata only — not enforced yet. */
export type PermissionTier = 'Read' | 'Propose' | 'Apply' | 'Send';

export interface StepResult {
  stepIndex: number;
  toolName: string;
  success: boolean;
  result?: ToolResult;
  error?: string;
  /** CORNER_V2: permission tier of the tool (from tool registry). */
  permissionTier?: PermissionTier;
  /** CORNER_V2: whether this step required user confirmation before execution. */
  requiresConfirmation?: boolean;
}

export interface OrchestrateResult {
  sessionId: string;
  plan: ChefPlan;
  steps: StepResult[];
  finalResult: ToolResult | null;
  message: string;
}

export interface OrchestratePreferencesData extends IntegrationPreferences {}

export type OrchestrateEventType =
  | 'planning'
  | 'assistant_placeholder'
  | 'plan_ready'
  | 'clarification'
  | 'clarification_step'
  | 'edit_batch'
  | 'step_start'
  | 'step_complete'
  | 'step_error'
  | 'done'
  | 'error'
  | 'thinking_chunk';

/** Category hint for step icons in the CoT timeline. */
export type StepCategory =
  | 'file'
  | 'search'
  | 'edit'
  | 'web'
  | 'draft'
  | 'sign'
  | 'check'
  | 'flag'
  | 'clarify';

/** Error codes for CoT error classification. Additive — raw `error` string always present as fallback. */
export type CoTErrorCode =
  | 'RATE_LIMIT'
  | 'QUOTA_EXCEEDED'
  | 'TIMEOUT'
  | 'TOOL_FAILED'
  | 'TOOL_NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'AUTH_REQUIRED'
  | 'API_ERROR'
  | 'FILE_ERROR'
  | 'FORMAT_ERROR'
  | 'FIELD_NOT_FOUND'
  | 'SIGNATURE_FIELD_NOT_FOUND'
  | 'FIELD_ALREADY_SIGNED'
  | 'PDF_PROCESSING_ERROR'
  | 'UNSUPPORTED_FORMAT'
  | 'FILE_TOO_SMALL'
  | 'FILE_EMPTY'
  | 'NO_DOCUMENT_ID'
  | 'NO_DOCUMENT'
  | 'NO_FILE_SELECTED'
  | 'USER_CANCELLED'
  | 'UNKNOWN';

export interface OrchestrateEvent {
  type: OrchestrateEventType;
  sessionId?: string;
  message?: string;
  plan?: ChefPlan;
  stepIndex?: number;
  tool?: string;
  description?: string;
  result?: ToolResult;
  error?: string;
  /** Typed error classification — additive alongside raw `error` string */
  errorCode?: CoTErrorCode;
  /** Whether the failed step/operation can be retried. Backend owns this decision. */
  retryable?: boolean;
  /** Whether this is a step-level failure or a run-level failure. */
  failureScope?: 'step' | 'run';
  question?: string;
  finalResult?: ToolResult | null;
  allSteps?: StepResult[];
  /** For thinking_chunk events: the streamed text fragment */
  chunk?: string;
  /** CORNER_V2: permission tier of the tool being executed (attached to step_start events). */
  permissionTier?: PermissionTier;
  /** CORNER_V2: whether this step requires user confirmation before execution. */
  requiresConfirmation?: boolean;
  /** Step category for icon rendering in the CoT timeline. */
  category?: StepCategory;
  /** For clarification_step: quick-reply button labels. */
  options?: string[];
  /** For clarification_step: whether to show a free-text input. */
  allowFreeInput?: boolean;
  /** For edit_batch events/results: staged document edits awaiting user action. */
  editBatch?: EditBatch;
  /**
   * Stable client-side id for the assistant row for this orchestration run.
   * Emitted once at `assistant_placeholder` (phase `planning`) and echoed on `done`
   * so the UI can create an empty bubble early and attach the final tool result to the same id.
   */
  assistantMessageId?: string;
  /** When type is `assistant_placeholder`, indicates emit timing (currently always `planning`). */
  phase?: 'planning' | 'plan_ready';
}

// ─── Multi-party E-Sign ─────────────────────────────────────────────

export type SigningRequestStatus = 'pending' | 'partially_signed' | 'completed' | 'expired' | 'revoked';
export type SignerStatus = 'pending' | 'viewed' | 'signed';
export type SigningAuditAction =
  | 'created'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'completed'
  | 'reminder_sent'
  | 'revoked';

export interface SignerSummary {
  email: string;
  name: string;
  role: string;
  status: SignerStatus;
  emailStatus?: 'pending' | 'sent' | 'failed';
  viewedAt?: string;
  signedAt?: string;
  lastReminderSentAt?: string;
}

export interface SigningRequestSummary {
  requestId: string;
  /** Source ProcessedFile id for the unsigned package (SSE + correlation). */
  documentFileId?: string;
  /** Output file id after all signatures are embedded; present when completed. */
  signedDocumentFileId?: string;
  /**
   * Field snapshot with completion state (`placed`, `signedAt`, `printedText`).
   * Included on `GET /api/signing/:requestId` only — omit on list payloads to stay small.
   */
  fields?: SignatureField[];
  documentName: string;
  status: SigningRequestStatus;
  signers: SignerSummary[];
  createdAt: string;
  expiresAt?: string;
  completedAt?: string;
  signedCount: number;
  totalCount: number;
  message?: string;
  senderName?: string;
}

export interface SigningTokenInfo {
  requestId: string;
  documentName: string;
  pageCount: number;
  senderName: string;
  senderEmail?: string;
  signerName: string;
  signerEmail: string;
  signerRole: string;
  fields: SignatureField[];
  requireEmailVerification: boolean;
  message?: string;
  expiresAt?: string;
  status: SigningRequestStatus;
  signerStatus: SignerStatus;
}

export interface SigningAuditEvent {
  action: SigningAuditAction;
  actorEmail: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// ═══════════════════════════════════════════════
// REAL-TIME COLLABORATION
// ═══════════════════════════════════════════════

export type CollaboratorPermission = 'view' | 'comment' | 'edit';
export type CommentPinStatus = 'open' | 'resolved';

// ─── Presence ───────────────────────────────────

export interface PresenceUser {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  color: string;
}

// ─── Live Cursors ───────────────────────────────

export interface CursorPosition {
  userId: string;
  displayName: string;
  color: string;
  x: number;
  y: number;
  pageNumber: number;
}

// ─── Document Comments ──────────────────────────

export interface DocCommentData {
  commentId: string;
  body: string;
  authorId?: string;
  authorName: string;
  authorColor: string;
  createdAt: string;
}

export interface CommentPinData {
  pinId: string;
  conversationId: string;
  authorId: string;
  authorName: string;
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  sectionRef?: string;
  quote?: string;
  status: CommentPinStatus;
  comments: DocCommentData[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommentPinRequest {
  pageNumber: number;
  xPercent: number;
  yPercent: number;
  sectionRef?: string;
  quote?: string;
  body: string;
}

export interface ReplyToCommentRequest {
  body: string;
}

// ─── Document Sharing ───────────────────────────

export interface CollaboratorSummary {
  userId?: string;
  email: string;
  displayName: string;
  permission: CollaboratorPermission;
  avatarUrl?: string;
  invitedAt: string;
  acceptedAt?: string;
}

export interface DocumentShareData {
  shareId: string;
  conversationId: string;
  ownerId: string;
  collaborators: CollaboratorSummary[];
  linkEnabled: boolean;
  linkPermission: CollaboratorPermission;
}

export interface InviteCollaboratorRequest {
  email: string;
  displayName: string;
  permission: CollaboratorPermission;
}

export interface UpdateCollaboratorRequest {
  permission: CollaboratorPermission;
}

// ═══════════════════════════════════════════════
// BILLING
// ═══════════════════════════════════════════════

export type SubscriptionPlanType = 'free' | 'pro' | 'enterprise';
export type SubscriptionStatusType = 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
export type BillingCycleType = 'monthly' | 'yearly';

export interface PaymentMethodInfo {
  type: 'card' | 'bank' | 'other';
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

export interface BillingStatusData {
  plan: SubscriptionPlanType;
  subscription: {
    id: string;
    status: SubscriptionStatusType;
    plan: string;
    billingCycle: BillingCycleType;
    currentPeriodEnd: string;
    trialEnd?: string | null;
    cancelAtPeriodEnd: boolean;
    paymentMethod?: PaymentMethodInfo | null;
  } | null;
  usage: UsageSummaryData;
  limits: {
    aiConversations: number | null;
    fileOperations: number | null;
    esignDocuments: number | null;
    filesPerConversation: number;
    maxFileSizeMB: number;
  };
}

export interface UsageSummaryData {
  month: string;
  aiConversations: number;
  fileOperations: number;
  esignDocuments: number;
}

export interface CheckoutRequest {
  plan: 'pro' | 'enterprise';
  cycle: BillingCycleType;
}

export interface CheckoutResponse {
  url: string;
}

export interface PortalResponse {
  url: string;
}

// ─── Clause Flagging ────────────────────────────────────────────────

export type ClauseFlagLane = 'real_estate' | 'founder_operator';

export type ClauseFlagSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export type ClauseFlagReviewBasis =
  | 'playbook_deviation'
  | 'missing_clause'
  | 'state_law_concern'
  | 'one_sided_risk'
  | 'ambiguity'
  | 'business_risk';

export type ClauseFlagConfidence = 'high' | 'medium' | 'low';

export type ClauseFlagStatus = 'pending' | 'accepted' | 'dismissed' | 'escalated';

export type ClauseReviewStatus = 'draft' | 'analyzing' | 'ready' | 'completed' | 'error';

export interface ClauseFlag {
  flagId: string;
  clauseType: string;
  severity: ClauseFlagSeverity;
  reviewBasis: ClauseFlagReviewBasis;
  confidence: ClauseFlagConfidence;
  confidenceReason: string | null;
  issue: string;
  evidenceQuote: string;
  sectionRef: string;
  sectionTitle: string;
  rationale: string;
  suggestedFallback: string;
  requiresHumanReview: boolean;
  jurisdictionNote: string | null;
  playbookRuleId: string | null;
  tags: string[];
  status: ClauseFlagStatus;
  /** Estimated page number where evidenceQuote appears (null if unavailable) */
  pageHint?: number | null;
}

export interface ClauseReviewSummary {
  clauseReviewId: string;
  title: string;
  sourceName: string;
  status: ClauseReviewStatus;
  lane: ClauseFlagLane;
  contractType: string;
  jurisdiction: string | null;
  jurisdictionSupported: boolean;
  playbookId: string;
  flags: ClauseFlag[];
  executiveSummary: string | null;
  modelUsed: string;
  lastAnalyzedAt: string | null;
  createdAt: string;
  stats: ClauseReviewStats;
  /** Coverage metadata — indicates whether the full document was reviewed. */
  coverage: ClauseReviewCoverage;
  /** Whether this review was user-initiated or auto-generated on import */
  triggerSource?: 'user' | 'auto';
}

export interface ClauseReviewCoverage {
  /** Total characters in the extracted document text. */
  textLength: number;
  /** Characters actually sent to the model for review. */
  reviewedCharacters: number;
  /** True if the document was truncated before review. */
  truncated: boolean;
}

export interface ClauseReviewStats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  accepted: number;
  dismissed: number;
  escalated: number;
  pending: number;
  requiresHumanReview: number;
}

// ─── Clause Flag Locate Result ──────────────────────

export interface ClauseFlagLocateResult {
  /** Whether a reliable snippet match was found */
  matched: boolean;
  /** Page where the match was found (may differ from original pageHint) */
  page: number | null;
  /** Text before the matched snippet (context window) */
  before: string;
  /** The matched snippet itself */
  snippet: string;
  /** Text after the matched snippet (context window) */
  after: string;
  /** The full page text for display (null if no page structure detected) */
  pageText: string | null;
  /** Character offset of the match start within pageText */
  matchStartInPage: number | null;
  /** Character offset of the match end within pageText */
  matchEndInPage: number | null;
}

// ─── Project Issues Tab ─────────────────────────────

/**
 * One open clause-flag row as it appears in the project Issues tab.
 *
 * This is a flattened, panel-friendly projection of a `ClauseFlag`
 * coupled with the minimum context the panel needs to render the row
 * and route the "Open in doc" click back into the existing flag UI:
 * the originating `reviewId` (for the locate / patch endpoints) and
 * the source `versionId` (for the version-rail switch).
 */
export interface ProjectClauseFlagSummary {
  /** Originating ClauseReview document — needed for locate/patch calls. */
  reviewId: string;
  /** Stable per-flag id within the review. */
  flagId: string;
  severity: ClauseFlagSeverity;
  /** One-line title rendered in the row. */
  issue: string;
  /**
   * Truncated evidence quote, ~200 chars max. Trimmed server-side so
   * the panel can render without a per-flag fetch.
   */
  evidenceQuote: string;
  sectionRef: string;
  pageHint: number | null;
  status: ClauseFlagStatus;
}

/**
 * One project version's group of open flags. Versions with zero open
 * flags are omitted from the response entirely.
 */
export interface ProjectClauseFlagGroup {
  /** DocumentVersion id (matches the version rail's version.id). */
  versionId: string;
  /** Display label, e.g. "Purchase Agreement · v2". */
  versionLabel: string;
  /** Underlying file id of the version. */
  fileId: string;
  /** Version number for stable sorting on the client. */
  versionNumber: number;
  /** Flags in severity order (critical → info). */
  flags: ProjectClauseFlagSummary[];
}

/**
 * Response shape for `GET /api/projects/:projectId/clause-flags`.
 *
 * The endpoint backs the Issues tab MVP (Scope 1: clause flags only).
 * Future scopes (signatures, comments) get their own response shapes.
 */
export interface ProjectClauseFlagsResponse {
  /** Total number of open flags across all groups. */
  totalCount: number;
  /**
   * Whether any open flag has severity `critical` or `high`. The
   * client uses this to color the Issues tab badge red.
   */
  hasCriticalOrHigh: boolean;
  /** Groups, sorted by versionNumber descending (newest version first). */
  groups: ProjectClauseFlagGroup[];
}

// ─── Clause Risk Taxonomy ───────────────────────────

/**
 * Risk categories that group clause types into user-facing risk areas.
 * Used for auto-surfacing high-risk flags after contract review analysis.
 */
export type ClauseRiskCategory =
  | 'liability'          // Liability, indemnification, hold-harmless
  | 'financial'          // Payment terms, deposits, fees, financial exposure
  | 'termination'        // Termination, renewal, exit, cancellation
  | 'ip_confidentiality' // IP assignment, confidentiality, trade secrets
  | 'restrictive'        // Non-compete, non-solicit, exclusivity
  | 'operational'        // Property condition, inspections, use restrictions
  | 'dispute'            // Governing law, dispute resolution, jurisdiction
  | 'other';             // Uncategorized flags

/**
 * Proactive nudge level for risk surfacing.
 * - silent: no badge, visible only inside clause review
 * - attention: calm badge — "flagged for your attention"
 * - reconsider: stronger badge — "you may want to reconsider before proceeding"
 */
export type ClauseNudgeLevel = 'silent' | 'attention' | 'reconsider';

/**
 * Compact risk summary computed from ClauseFlags.
 * Used to show a risk badge on the AnalysisCard after contract review.
 */
export interface ClauseRiskSummary {
  /** Whether any flags cross the auto-surface threshold */
  hasHighRisk: boolean;
  /** Count of flags at critical or high severity */
  highRiskCount: number;
  /** Count of flags at medium severity */
  mediumRiskCount: number;
  /** Count of flags at low/info severity */
  lowRiskCount: number;
  /** Top risk categories present (max 3, ordered by flag count) */
  topCategories: ClauseRiskCategory[];
  /** One-line summary for display (e.g., "3 high-risk clauses in liability, termination") */
  label: string;
  /** Proactive nudge level — controls badge styling and copy */
  nudgeLevel: ClauseNudgeLevel;
  /** Lane for display context */
  lane: ClauseFlagLane;
  /** Jurisdiction if available */
  jurisdiction: string | null;
  /** The clause review ID these flags belong to */
  clauseReviewId: string;
}

// ─── Risk Preferences ───────────────────────────────

/**
 * Per-user risk preferences for clause review.
 * Allows overriding default severity for specific clause types.
 */
export interface RiskPreferences {
  /** Per-clause-type severity overrides (e.g., { non_compete: 'critical' }) */
  severityOverrides: Record<string, ClauseFlagSeverity>;
  /** Whether auto-scan is enabled for imported documents */
  autoScanEnabled: boolean;
}

// ─── Clause Grounding Status ────────────────────────

/**
 * Per-flag grounding status — mirrors CoT partial completion semantics.
 * Tracks whether a flag's evidence quote could be located in the source document.
 *
 * - 'exact':   evidenceQuote found and located precisely
 * - 'approx':  approximate or fuzzy match (whitespace/case normalized)
 * - 'missing': could not be confidently located in the document
 * - 'unknown': not yet checked (user hasn't clicked "Locate" for this flag)
 */
export type FlagGroundingStatus = 'exact' | 'approx' | 'missing' | 'unknown';

/**
 * Per-review grounding summary — computed from individual flag statuses.
 * Used to show an honest "partial success" indicator in the clause review header.
 */
export interface ClauseReviewGroundingSummary {
  /** True when all checked flags have exact matches */
  allExact: boolean;
  /** True when at least one flag has an approximate match */
  anyApprox: boolean;
  /** True when at least one flag could not be located */
  anyMissing: boolean;
  /** How many flags have been checked (located) */
  checkedCount: number;
  /** Total number of flags in the review */
  totalCount: number;
}

// ─── Document Editing Model ─────────────────────────────────────────────────
//
// Architecture alignment:
// - Text extraction: lib/pdfTextExtract.ts + lib/extractDocumentText.ts produce
//   raw text per page. EditableDoc normalizes this into logical blocks.
// - DocumentFieldMap: stores detected fields (signatures, dates, placeholders)
//   with 1-indexed pages and percent-based bboxes. EditableDoc references the
//   same page indexing and links to field IDs via placeholderId.
// - textToPdf: converts plain text → PDF for signing. EditableDoc can be
//   serialized back to text for this pipeline.

/** Types of edits that can be applied to a document */
export type DocEditActionType =
  | 'replace_paragraph'       // replace entire paragraph text
  | 'insert_paragraph'        // insert new paragraph at a position
  | 'delete_paragraph'        // remove a paragraph
  | 'replace_range'           // replace text within a paragraph (future)
  | 'set_placeholder_value';  // fill a detected placeholder by field ID

/** Structural location within a document (not coordinate-based) */
export interface DocLocationByStructure {
  /** 1-indexed page number (consistent with DetectedDocField.page) */
  page?: number;
  /** Logical paragraph index within the page or document */
  paragraphIndex?: number;
  /** Line index within a paragraph (for fine-grained edits, future) */
  lineIndex?: number;
  /** Character offset within the targeted paragraph/block for fine-grained edits. */
  startOffset?: number;
  /** Character offset within the targeted paragraph/block for fine-grained edits. */
  endOffset?: number;
}

/** A single edit action to apply to a document */
export interface DocEditAction {
  /** What kind of edit */
  type: DocEditActionType;
  /** Where in the document structure */
  target: DocLocationByStructure;
  /** New text content (for replace/insert) */
  newText?: string;
  /** Original text slice for range-level replacements and format-preserving renderers. */
  originalText?: string;
  /** ID from DocumentFieldMap (links to DetectedDocField.id for placeholder fills) */
  placeholderId?: string;
  /** Semantic role from DocFieldRole (e.g. 'buyer_name', 'agreement_date') */
  semanticRole?: string;
  /** Value to set (for set_placeholder_value) */
  value?: string;
  /**
   * Optional plain-language summary from the planner (shown on review cards).
   * Example: "Set the seller's name to Bogdan B. in the preamble."
   */
  intentSummary?: string;
}

export type EditStatus = 'pending' | 'accepted' | 'rejected' | 'superseded';

export type DiffSegment =
  | { kind: 'context'; text: string }
  | { kind: 'insert'; text: string }
  | { kind: 'delete'; text: string };

export interface EditLocation {
  docId: string;
  /** Stable structural anchor, e.g. "p1:block4". */
  anchor: string;
  page?: number;
  paragraphIndex?: number;
}

export interface EditVersionArtifact {
  docId: string;
  version: number;
  filename: string;
  fileId?: string;
  downloadUrl?: string;
  mimeType?: string;
}

/**
 * Why a single proposed PDF edit is or is not safe to apply as a
 * layout-preserving overlay.
 *
 * Categories:
 *   - `ok`                         : applicable.
 *   - `anchor_not_found` /
 *     `ambiguous_anchor` /
 *     `cross_line` / `cross_block` /
 *     `bbox_overflow` /
 *     `layout_shift_risk` /
 *     `unverified_replacement`     : per-edit "unsafe" — the edit cannot
 *                                    be applied without changing the
 *                                    document's visible layout. UI should
 *                                    let the user review the proposed
 *                                    text and apply it manually.
 *   - `source_pdf_missing` /
 *     `verification_unavailable` /
 *     `infra_unavailable`          : per-batch "we couldn't run the check".
 *                                    These are infra failures, not safety
 *                                    failures. UI should offer a retry
 *                                    rather than show "this edit is unsafe".
 *
 * The split between "unsafe" and "infra" is the whole reason for keeping
 * separate reason codes: collapsing infra failures into
 * `unverified_replacement` was the production bug that blocked every
 * accept after Render's ephemeral disk dropped a source PDF.
 */
export type PdfOverlayEligibilityReason =
  | 'ok'
  | 'anchor_not_found'
  | 'ambiguous_anchor'
  | 'cross_line'
  | 'cross_block'
  | 'bbox_overflow'
  | 'layout_shift_risk'
  | 'unverified_replacement'
  | 'verification_unavailable'
  | 'source_pdf_missing'
  | 'infra_unavailable';

export interface PdfOverlayEligibility {
  overlayEligible: boolean;
  reason: PdfOverlayEligibilityReason;
  page?: number;
  anchorId?: string;
  bbox?: { x: number; y: number; width: number; height: number };
  /** Which edit tier this verified PDF edit will use. */
  tier?: DocumentEditApplyTier;
}

export type DocumentEditPolicy =
  | 'signed_final_pdf'
  | 'unsigned_review_pdf'
  | 'structured_source_doc';

export type DocumentEditApplyTier =
  | 'exact_overlay'
  | 'local_reflow'
  | 'source_native';

/**
 * Phase 2: canonical anchor for an edit, populated by the unified
 * anchor+verify pass on the backend. Separates "which block of the
 * editable doc" (structural) from "which run of glyphs in the PDF"
 * (geometric). Optional fields are populated only when the relevant
 * pipeline step succeeds — e.g. `pdfTextRunId` / `bbox` are only set
 * for verified PDF edits.
 */
export interface EditAnchor {
  /** Source representation we anchored against. */
  kind: 'text_block' | 'pdf_text_run' | 'placeholder' | 'html_node';
  /** 1-indexed page number for paginated sources. */
  page?: number;
  paragraphIndex?: number;
  /** Stable structural anchor: `p{page}:block{idx}` for editable docs. */
  blockAnchorId: string;
  /** PDF-specific run anchor: `p{page}:idx{idx}:hash{hash}`. */
  pdfTextRunId?: string;
  /** Bounding box of the anchored text run in PDF user units. */
  bbox?: { x: number; y: number; width: number; height: number };
  /** What the anchor pass actually matched in the source. */
  matchedText?: string;
}

/**
 * Phase 2: a single fragment of original text that an apply pass needs
 * to white out and redraw. Cached on `edit.verification.fragments` so
 * the apply path can replay the verified plan without re-running text
 * matching. Geometry is in PDF user units, top-left origin.
 */
export interface PdfFragmentDescriptor {
  /** Index of the source `pdf.js` text item, for diagnostics. */
  itemIndex: number;
  /** Slice of the item's text that's part of this fragment. */
  text: string;
  /** Visual rectangle to white out before drawing the replacement. */
  rect: { x: number; y: number; width: number; height: number };
  /** Glyph baseline used to reproduce vertical placement. */
  baselineY: number;
  /** Font size derived from the text item transform. */
  fontSize: number;
}

/**
 * Phase 2: verification status for a single edit. Drives the per-edit
 * UI affordances on the inline review card; fragments are kept here so
 * the apply path is a pure replay of an already-verified plan.
 */
export interface EditVerification {
  status: 'unverified' | 'verified' | 'failed';
  /** When `failed`, why — same taxonomy as `PdfOverlayEligibilityReason`. */
  reason?: PdfOverlayEligibilityReason;
  /** Whether the replacement text fits the anchored bbox without overflow. */
  fitMode?: 'fits' | 'shrunk' | 'overflow' | 'local_reflow';
  /** Applied/verified edit tier. */
  tier?: DocumentEditApplyTier;
  /** Cached overlay plan, replayed on apply. Empty for non-PDF batches. */
  fragments?: PdfFragmentDescriptor[];
  /** Epoch ms at which this verification ran. */
  verifiedAt?: number;
}

export interface Edit {
  id: string;
  description: string;
  diff: DiffSegment[];
  location: EditLocation;
  status: EditStatus;
  groupId?: string;
  /** Internal target integrity check used by the backend to detect stale edits. */
  originalHash?: string;
  /** Internal action payload used by the backend accept path. */
  action?: DocEditAction;
  /** Internal action payload used by in-conversation HTML draft review/apply. */
  htmlAction?: HtmlEditAction;
  /**
   * Phase 1 BC alias, kept so existing consumers (wizard, clause flagging,
   * older snapshots) keep working. Computed from `verification` on the
   * Phase 2 backend; new consumers should read `verification` directly.
   */
  pdfOverlayEligibility?: PdfOverlayEligibility;
  /** Phase 2 canonical anchor (structural + optional PDF geometry). */
  anchor?: EditAnchor;
  /** Phase 2 canonical verification + cached overlay plan. */
  verification?: EditVerification;
}

export type HtmlEditActionKind = 'replace' | 'insertBefore' | 'insertAfter' | 'delete';

export type HtmlEditAction =
  | {
      id: string;
      kind: 'replace';
      label: string;
      explanation?: string;
      originalText: string;
      newText: string;
      selector?: string;
    }
  | {
      id: string;
      kind: 'insertBefore' | 'insertAfter';
      label: string;
      explanation?: string;
      anchorText: string;
      newText: string;
      selector?: string;
    }
  | {
      id: string;
      kind: 'delete';
      label: string;
      explanation?: string;
      originalText: string;
      selector?: string;
    };

/**
 * Phase 2: explicit lifecycle for a staged batch. Mirrors the frontend
 * `CardState` so the inline review card is a direct projection of
 * server-authored status. `pending` means "waiting for user review";
 * `applying` is set transiently while the accept route is in flight.
 */
export type BatchStatus =
  | 'pending'
  | 'applying'
  | 'applied'
  | 'partial'
  | 'blocked_unsafe'
  | 'blocked_infra'
  | 'verification_unavailable'
  | 'error';

export interface EditBatch {
  id: string;
  edits: Edit[];
  resultingVersion: EditVersionArtifact;
  /** Canonical document lineage/project id this batch was planned against. */
  lineageId?: string | null;
  /** Canonical immutable version id this batch was planned against. */
  baseVersionId?: string | null;
  /** Canonical immutable version id created after accepting the batch. */
  resultingVersionId?: string | null;
  /** Refreshed canonical lineage after accepting the batch. */
  resultingProject?: DocumentProjectDetail | null;
  /** Source document identity for display/debugging. */
  docId: string;
  sourceFilename?: string;
  sourceMimeType?: string;
  applyMode?: 'editable_doc_artifact' | 'conversation_html_revision';
  conversationId?: string;
  baseDocumentId?: string;
  baseDocumentVersion?: number | string;
  draftTitle?: string;
  plannedAgainstFileId?: string;
  plannedAgainstVersionId?: number | string | null;
  plannedAgainstModelType?: string;
  plannedTextHash?: string;
  /** Product policy selected for this batch. */
  editPolicy?: DocumentEditPolicy;
  /** Phase 2 batch-level state machine. Drives both server and UI. */
  status?: BatchStatus;
  /** Phase 2: epoch ms when the canonical anchor+verify pass last ran. */
  verifiedAt?: number;
  /** Phase 2: most recent apply attempt outcome (null until accept runs). */
  lastOutcome?: EditApplyOutcome | null;
  applyFailure?: {
    code: string;
    reason: string;
    details?: Record<string, unknown>;
  };
}

/** Per-edit reason returned alongside an `EditApplyOutcome` blocked state. */
export interface EditApplyBlockedDetail {
  editId: string;
  reason: PdfOverlayEligibilityReason;
  page?: number | null;
}

/**
 * Discriminated union returned by `accept-all` (and `accept`) so the
 * frontend can drive an explicit state machine instead of inferring
 * state from a generic `EditBatch.applyFailure` blob.
 *
 * Frontend mapping:
 *   - applied / partial               -> success / partial-success card
 *   - blocked_unsafe                  -> review proposed text manually
 *   - blocked_infra                   -> retry button (we'll re-run
 *                                       eligibility on the next attempt)
 *   - verification_unavailable        -> "PDF can't be verified"
 *                                       (encrypted/scanned/corrupt)
 *   - error                           -> unexpected failure
 */
export type EditApplyOutcome =
  | {
      kind: 'applied';
      batchId: string;
      appliedCount: number;
      fileId?: string;
      versionId?: string | null;
      projectId?: string | null;
    }
  | {
      kind: 'applied_exact';
      batchId: string;
      appliedCount: number;
      fileId?: string;
      versionId?: string | null;
      projectId?: string | null;
    }
  | {
      kind: 'applied_local_reflow';
      batchId: string;
      appliedCount: number;
      fileId?: string;
      versionId?: string | null;
      projectId?: string | null;
    }
  | {
      kind: 'partial';
      batchId: string;
      appliedCount: number;
      blocked: EditApplyBlockedDetail[];
      fileId?: string;
      versionId?: string | null;
      projectId?: string | null;
    }
  | {
      kind: 'blocked_unsafe';
      batchId: string;
      blocked: EditApplyBlockedDetail[];
      detail?: string;
    }
  | {
      kind: 'blocked_overflow';
      batchId: string;
      blocked: EditApplyBlockedDetail[];
      detail?: string;
    }
  | {
      kind: 'blocked_final_document';
      batchId: string;
      blocked: EditApplyBlockedDetail[];
      detail?: string;
    }
  | {
      kind: 'blocked_infra';
      batchId: string;
      detail: string;
      canRetry: boolean;
      blocked?: EditApplyBlockedDetail[];
    }
  | {
      kind: 'verification_unavailable';
      batchId: string;
      detail: string;
      blocked?: EditApplyBlockedDetail[];
    }
  | {
      kind: 'error';
      batchId: string;
      detail: string;
      code?: string;
    };

export interface DocEditRecoveryResult {
  editBatch: EditBatch;
  project: DocumentProjectDetail | null;
  versionId: string | null;
  fileId: string;
  /** True when Corner materialized a normalized EditableDoc for in-app editing. */
  hasEditableDoc?: boolean;
  recovery: {
    kind: 'editable_draft_docx';
    sourceFinalFileId: string;
    selectedVersionId: string | null;
  };
}

export interface HtmlEditApplyResult {
  editBatch: EditBatch;
  documentId: string;
  documentVersion: number;
  draftHtml: string;
  draftTitle: string;
  appliedIds?: string[];
  failedIds?: string[];
}

/** Source format of the original document before normalization */
export type EditableSourceFormat = 'pdf' | 'docx' | 'txt' | 'image' | 'html';

/** A single logical text block in the normalized document */
export interface EditableDocBlock {
  /** 1-indexed page number (consistent with DetectedDocField.page) */
  page: number;
  /** Index of this block within the document (global ordering) */
  paragraphIndex: number;
  /** The text content of this block */
  text: string;
}

/**
 * A normalized, editable representation of a document.
 * Produced by parsing the original file (PDF, DOCX, TXT) into logical blocks.
 * Can be used for:
 * - AI-driven editing (replace/insert/delete paragraphs)
 * - Placeholder filling (linked via field IDs from DocumentFieldMap)
 * - Rendering back to the original format
 */
export interface EditableDoc {
  /** Unique document identifier (matches ProcessedFile.fileId or conversation doc) */
  docId: string;
  /** Original file format */
  sourceFormat: EditableSourceFormat;
  /** Ordered list of text blocks */
  blocks: EditableDocBlock[];
}

// ─── Document Edit Snapshot (for LLM planning) ─────────────────────────────

/** Lightweight block view for the LLM planner */
export interface DocEditSnapshotBlock {
  page: number;
  paragraphIndex: number;
  text: string;
}

/** Placeholder field from DocumentFieldMap, for the LLM to reference by ID */
export interface DocEditSnapshotPlaceholder {
  /** DetectedDocField.id — used as placeholderId in set_placeholder_value actions */
  id: string;
  page: number;
  /** Paragraph index in the EditableDoc (null if not yet mapped) */
  paragraphIndex?: number | null;
  /** Raw placeholder text, e.g. 'BUYER FULL NAME' */
  placeholderRaw?: string | null;
  /** Semantic role from classification, e.g. 'buyer_name' */
  semanticRole?: string | null;
}

/**
 * A snapshot of the document state for the LLM doc-edit planner.
 * Contains the text blocks + placeholder metadata so the LLM can
 * output precise DocEditAction[] targeting specific blocks/fields.
 */
export interface DocEditSnapshot {
  docId: string;
  blocks: DocEditSnapshotBlock[];
  placeholders: DocEditSnapshotPlaceholder[];
}

/**
 * A user-selected text snippet from the document viewer.
 * Used as context for "ask about selection" interactions:
 * the AI treats this snippet as the primary anchor for edits/analysis.
 */
export interface DocSelectedSnippet {
  /** The raw text the user highlighted */
  text: string;
  /** Page number where the selection occurred (1-indexed, if known) */
  page?: number;
  /** ParagraphIndex in EditableDoc (if known from the viewer) */
  paragraphIndex?: number;
}
